import { clearCheckoutHoldSession, readCheckoutHoldSession } from "./checkoutHoldSession";
import { clearSmartRouteSlot, readSmartRouteSlot } from "./smartRouteSlotStorage";
import { formatTravelTimeLabel, parsePickupSlotInstant } from "./computeTravelSlotTiming";
import { getDriveMinutesFromDeparture } from "./travelEstimates";
import {
  archiveDriveTripToHistory,
  historyTripToPlan,
  loadDriveTripHistory,
} from "./driveTripHistory";
import {
  parkDriveTrip,
  getCurrentDriveOrderedRestaurantIds,
  markCurrentDriveTripOrderReady,
  markCurrentDriveTripOrderTaken,
  loadCurrentDriveTrips,
  updateCurrentDriveTrip,
  removeCurrentDriveTrip,
  clearInProcessDriveTripForEndpoints,
  dismissAllInProcessDriveTrips,
  dismissInProcessDriveTripsExceptOrigin,
  isInProcessDriveTrip,
  saveCurrentDriveTrips,
} from "./driveCurrentTrips";
import { clearActiveDriveTrip, isNearMeExploreMode, loadActiveDriveTrip } from "./driveFavoriteLaunch";
import { loadLastTravelTrip } from "./travelAddressHistory";
import { DRIVE_MULTI_STOP_ENABLED } from "../constants/featureFlags";

// Lazy notify so history UI refreshes without a circular import on the React component.
function notifyTripHistoryUi() {
  try {
    window.dispatchEvent(new Event("smartRoute:tripHistoryChanged"));
  } catch {
    /* ignore */
  }
}

function notifyMultiStopPlanUi() {
  try {
    window.dispatchEvent(new Event("smartRoute:multiStopPlanChanged"));
  } catch {
    /* ignore */
  }
}

const LS_PLAN = "smartRoute:multiStopPlan_v1";
export const MAX_DRIVE_STOPS = 3;
/** Minutes to collect food at each stop before continuing. */
export const DRIVE_STOP_DWELL_MINUTES = 5;
/** TTL only for browsing / empty plans — unfinished paid pickups must not expire. */
const PLAN_TTL_MS = 3 * 60 * 60 * 1000;
/** Allow slots within this window of notBefore (clock skew / rounding). */
const NOT_BEFORE_TOLERANCE_MS = 45 * 1000;

function rid(id) {
  return String(id || "").trim();
}

function shopName(shop) {
  return String(shop?.shopDetails?.shopName || shop?.name || "").trim() || "Restaurant";
}

/** True while any paid stop still awaits pickup (ordered or ready). */
export function hasUnfinishedPaidDriveStops(plan) {
  return (Array.isArray(plan?.stops) ? plan.stops : []).some((s) => {
    const status = String(s?.status || "");
    return status === "ordered" || status === "ready";
  });
}

export function loadDriveMultiStopPlan() {
  try {
    const raw = sessionStorage.getItem(LS_PLAN);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Do not TTL-wipe trips still waiting for prêt à récupérer or customer pickup slide.
    if (
      parsed?.savedAt &&
      Date.now() - parsed.savedAt > PLAN_TTL_MS &&
      !hasUnfinishedPaidDriveStops(parsed)
    ) {
      sessionStorage.removeItem(LS_PLAN);
      return null;
    }
    if (!parsed?.origin || !Array.isArray(parsed?.stops)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePlanToSession(plan, { notify = true, sync = true } = {}) {
  try {
    if (!plan) {
      sessionStorage.removeItem(LS_PLAN);
    } else {
      sessionStorage.setItem(LS_PLAN, JSON.stringify(plan));
    }
    if (notify) notifyMultiStopPlanUi();
    if (sync) {
      try {
        window.dispatchEvent(new Event("smartRoute:driveTripLocalChanged"));
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore */
  }
}

export function saveDriveMultiStopPlan(plan) {
  try {
    if (!plan?.origin) return null;
    const next = {
      ...plan,
      maxStops: MAX_DRIVE_STOPS,
      stops: Array.isArray(plan.stops) ? plan.stops.slice(0, MAX_DRIVE_STOPS) : [],
      savedAt: Date.now(),
    };
    writePlanToSession(next, { notify: true, sync: true });
    return next;
  } catch {
    return null;
  }
}

/**
 * Restaurant ids on an unpaid Global Drive draft (open stops still in the cart flow).
 */
export function unpaidGlobalDriveRestaurantIds(plan = loadDriveMultiStopPlan()) {
  if (!plan || getPaidStops(plan).length > 0) return [];
  const ids = [];
  for (const stop of getOrderedStops(plan)) {
    if (stop?.status !== "open") continue;
    const id = String(stop.restaurantId || "").trim();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * True when this restaurant still has Global Drive checkout state
 * (route slot, ASAP hold, or an open unpaid multi-stop stop).
 * Once Classic/feed already left Drive (`feedCartOrder` mark), do not ask again
 * until Explore reopens this stop and clears that mark.
 */
export function hasOpenGlobalDriveCheckoutSession(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return false;
  try {
    if (sessionStorage.getItem(`feedCartOrder:${rid}`) === "1") return false;
  } catch {
    /* ignore */
  }
  const slot = readSmartRouteSlot(rid);
  if (slot?.source === "drive" && !slot.restaurantDrive) return true;
  if (readCheckoutHoldSession(rid)) return true;
  return unpaidGlobalDriveRestaurantIds().includes(rid);
}

/**
 * A favorites / restaurant Drive order is its own trip.
 * Drop the unfinished Global Drive draft and any unpaid working plan so
 * checkout uses the address on this order — including ASAP slots/holds left on open stops.
 */
export function releaseUnpaidGlobalDriveDraft() {
  dismissAllInProcessDriveTrips();
  clearActiveDriveTrip();
  const plan = loadDriveMultiStopPlan();
  if (!plan || getPaidStops(plan).length > 0) return;
  for (const rid of unpaidGlobalDriveRestaurantIds(plan)) {
    clearSmartRouteSlot(rid);
    clearCheckoutHoldSession(rid);
  }
  clearDriveMultiStopPlan();
}

export function clearDriveMultiStopPlan() {
  writePlanToSession(null, { notify: true, sync: true });
}

/**
 * Apply server (or merged) plan into sessionStorage without pushing back immediately.
 */
export function hydrateDriveMultiStopPlan(plan, { notify = true } = {}) {
  if (!plan) {
    writePlanToSession(null, { notify, sync: false });
    return null;
  }
  if (!plan?.origin || !Array.isArray(plan?.stops)) {
    writePlanToSession(null, { notify, sync: false });
    return null;
  }
  const next = {
    ...plan,
    maxStops: MAX_DRIVE_STOPS,
    stops: plan.stops.slice(0, MAX_DRIVE_STOPS),
    savedAt: Number(plan.savedAt) || Date.now(),
  };
  writePlanToSession(next, { notify, sync: false });
  return next;
}

export function getOrderedStops(plan) {
  return [...(plan?.stops || [])].sort(
    (a, b) =>
      (Number(a.routeOffsetMinutes) || 0) - (Number(b.routeOffsetMinutes) || 0) ||
      String(a.restaurantId).localeCompare(String(b.restaurantId)),
  );
}

/** First stop that is not yet paid (browsing / in-progress checkout). */
export function getActiveOpenStop(plan) {
  return getOrderedStops(plan).find((s) => s.status === "open") || null;
}

/**
 * Drop unfinished open stops when the user leaves without ordering.
 * Add-on opens (already paid at that restaurant) are restored to "ordered".
 * Brand-new opens with no order are removed. Clears the plan if nothing remains
 * (search / browse without placing an order is never kept as a trip plan).
 */
export function abandonOpenDriveStops(plan = loadDriveMultiStopPlan()) {
  if (!plan?.stops?.length) {
    if (plan) clearDriveMultiStopPlan();
    return null;
  }

  let changed = false;
  const nextStops = [];
  for (const s of getOrderedStops(plan)) {
    if (s.status !== "open") {
      nextStops.push(s);
      continue;
    }
    changed = true;
    // Already paid here (add-more flow) — put the stop back to ordered.
    if (s.orderId || (Array.isArray(s.previousOrderIds) && s.previousOrderIds.length)) {
      nextStops.push({ ...s, status: "ordered" });
      continue;
    }
    // Never ordered — discard.
  }

  // No paid stops left → not a trip plan; clear entirely.
  if (!nextStops.some((s) => isPaidDriveStopStatus(s.status))) {
    clearDriveMultiStopPlan();
    return null;
  }

  if (!changed) return plan;

  return saveDriveMultiStopPlan({ ...plan, stops: nextStops });
}

/** Paid stop statuses: ordered → ready (restaurant notified) → taken. */
export function isPaidDriveStopStatus(status) {
  const s = String(status || "").toLowerCase();
  return s === "ordered" || s === "ready" || s === "taken";
}

/** Paid pickups: ordered / ready (awaiting collection) or already taken. */
export function getPaidStops(plan) {
  return getOrderedStops(plan).filter((s) => isPaidDriveStopStatus(s.status));
}

export function getTakenStops(plan) {
  return getOrderedStops(plan).filter((s) => s.status === "taken");
}

/** Stops the restaurant has marked ready to pick up. */
export function getReadyStops(plan) {
  return getOrderedStops(plan).filter((s) => s.status === "ready");
}

/** Order ids linked to one stop (current + same-stop add-ons). */
export function getStopOrderIds(stop) {
  if (!stop) return [];
  const ids = [];
  if (stop.orderId) ids.push(String(stop.orderId));
  if (Array.isArray(stop.previousOrderIds)) {
    for (const id of stop.previousOrderIds) {
      if (id) ids.push(String(id));
    }
  }
  return [...new Set(ids)];
}

function rawTokenNumbers(stop) {
  if (!stop) return [];
  const fromList = Array.isArray(stop.tokenNumbers)
    ? stop.tokenNumbers.map((t) => String(t || "").trim()).filter(Boolean)
    : [];
  const single = String(stop.tokenNumber || "").trim();
  const merged = [...fromList];
  if (single && !merged.includes(single)) merged.push(single);
  return [...new Set(merged)];
}

/** Token numbers for a stop (first order + same-stop add-ons), oldest → newest. */
export function getStopTokenNumbers(stop) {
  if (!stop) return [];
  if (Array.isArray(stop.pickupGroups) && stop.pickupGroups.length) {
    const fromGroups = stop.pickupGroups
      .flatMap((g) => (Array.isArray(g.tokenNumbers) ? g.tokenNumbers : []))
      .map((t) => String(t || "").trim())
      .filter(Boolean);
    if (fromGroups.length) return [...new Set(fromGroups)];
  }
  return rawTokenNumbers(stop);
}

function isDriveOrderRecord(order) {
  if (!order) return false;
  if (String(order.orderType || "").toUpperCase() === "DRIVE") return true;
  if (order.driveLeaveHomeAt) return true;
  const snap = order.drivePickupTimingSnapshot;
  return !!(snap && (snap.orderBeforeLabel || snap.leaveByLabel || snap.slotLabel));
}

function relatedOrderIdStrings(order) {
  return (Array.isArray(order?.driveRelatedOrderIds) ? order.driveRelatedOrderIds : [])
    .map((id) => rid(id?._id || id))
    .filter(Boolean);
}

/**
 * Orders added as extra articles on the same trip share one group.
 * Every other order stays in its own group.
 */
export function clusterAddOnOrders(orders) {
  const list = (Array.isArray(orders) ? orders : []).filter((o) => o && rid(o._id));
  const byId = new Map(list.map((o) => [rid(o._id), o]));
  const assigned = new Set();
  const groups = [];

  for (const order of list) {
    const seed = rid(order._id);
    if (assigned.has(seed)) continue;
    const queue = [seed];
    const group = [];
    while (queue.length) {
      const id = queue.shift();
      if (assigned.has(id)) continue;
      assigned.add(id);
      const doc = byId.get(id);
      if (!doc) continue;
      group.push(doc);
      for (const rel of relatedOrderIdStrings(doc)) {
        if (byId.has(rel) && !assigned.has(rel)) queue.push(rel);
      }
    }
    if (group.length) groups.push(group);
  }
  return groups;
}

function pickupGroupStatus(orders) {
  const statuses = (orders || []).map((o) => String(o?.status || "").toLowerCase());
  if (statuses.length && statuses.every((s) => s === "completed")) return "taken";
  if (statuses.some((s) => s === "ready")) return "ready";
  return "ordered";
}

function groupFromOrders(orders, shell) {
  const sorted = [...orders].sort((a, b) => {
    const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return ta - tb;
  });
  const ids = sorted.map((o) => rid(o._id)).filter(Boolean);
  const latest = sorted[sorted.length - 1] || {};
  return {
    key: [...ids].sort().join("__"),
    orderIds: ids,
    tokenNumbers: sorted.map((o) => String(o.tokenNumber || "").trim()).filter(Boolean),
    status: pickupGroupStatus(sorted),
    pickupWindow: latest.pickupWindow || shell?.pickupWindow || null,
    pickupDate: latest.pickupDate || shell?.pickupDate || null,
  };
}

/** Pickup slides for one stop. Add-on articles share a slide; other orders do not. */
export function pickupGroupsOf(stop) {
  if (Array.isArray(stop?.pickupGroups) && stop.pickupGroups.length) {
    return stop.pickupGroups.map((g) => ({
      key: String(g.key || (g.orderIds || []).join("__")),
      orderIds: (Array.isArray(g.orderIds) ? g.orderIds : []).map((id) => rid(id)).filter(Boolean),
      tokenNumbers: (Array.isArray(g.tokenNumbers) ? g.tokenNumbers : [])
        .map((t) => String(t || "").trim())
        .filter(Boolean),
      status: String(g.status || stop.status || "ordered"),
      pickupWindow: g.pickupWindow || stop.pickupWindow || null,
      pickupDate: g.pickupDate || stop.pickupDate || null,
    }));
  }
  const orderIds = getStopOrderIds(stop);
  const tokenNumbers = rawTokenNumbers(stop);
  if (!orderIds.length && !tokenNumbers.length) return [];
  // Several orders on one stop are separate pickups until we know they were
  // added as articles on this trip. That avoids one slide collecting the day.
  if (orderIds.length > 1) {
    return orderIds.map((id) => ({
      key: id,
      orderIds: [id],
      tokenNumbers: [],
      status: "ordered",
      pickupWindow: stop?.pickupWindow || null,
      pickupDate: stop?.pickupDate || null,
    }));
  }
  return [
    {
      key: orderIds.join("__") || rid(stop?.restaurantId) || "stop",
      orderIds,
      tokenNumbers,
      status: String(stop?.status || "ordered"),
      pickupWindow: stop?.pickupWindow || null,
      pickupDate: stop?.pickupDate || null,
    },
  ];
}

function stopSignature(plan) {
  if (!plan?.stops) return "";
  return JSON.stringify(
    plan.stops.map((s) => ({
      r: rid(s.restaurantId),
      status: s.status,
      date: s.pickupDate || "",
      window: s.pickupWindow || "",
      ids: getStopOrderIds(s).slice().sort(),
      groups: pickupGroupsOf(s).map((g) => ({
        ids: g.orderIds.slice().sort(),
        status: g.status,
        tokens: g.tokenNumbers,
        date: g.pickupDate || "",
        window: g.pickupWindow || "",
      })),
    })),
  );
}

function buildStopFromGroups(shell, pickupGroups) {
  if (!pickupGroups.length) return null;
  const allIds = pickupGroups.flatMap((g) => g.orderIds);
  const primary = allIds[allIds.length - 1] || shell.orderId || null;
  const active =
    pickupGroups.find((g) => g.status === "ready") ||
    pickupGroups.find((g) => g.status === "ordered") ||
    pickupGroups[pickupGroups.length - 1];
  const status = pickupGroups.every((g) => g.status === "taken")
    ? "taken"
    : active?.status || "ordered";
  const single = pickupGroups.length === 1;
  return {
    ...shell,
    status,
    orderId: primary,
    previousOrderIds: allIds.filter((id) => id !== String(primary || "")),
    tokenNumbers: single ? pickupGroups[0].tokenNumbers : [],
    tokenNumber: single
      ? pickupGroups[0].tokenNumbers[pickupGroups[0].tokenNumbers.length - 1] || null
      : null,
    pickupGroups,
    pickupWindow: active?.pickupWindow || shell.pickupWindow || null,
    pickupDate: active?.pickupDate || shell.pickupDate || null,
  };
}

/**
 * Articles added with "add more items" stay on that trip and share its tokens.
 * Any other order is its own trip with its own token, including a later trip
 * that uses the same From and To on the same day.
 *
 * @param {object|null} plan
 * @param {object[]} orders — server order rows (need _id, status, pickupDate, orderType, driveRelatedOrderIds)
 * @returns {{ plan: object|null, extraPlans: object[], changed: boolean }}
 */
export function splitDriveTripOrdersByDay(plan, orders) {
  if (!plan?.stops?.length) return { plan, extraPlans: [], changed: false };

  const byId = new Map();
  for (const order of Array.isArray(orders) ? orders : []) {
    const id = rid(order?._id);
    if (id) byId.set(id, order);
  }

  const homeDate = String(
    (plan.stops.find((s) => String(s?.pickupDate || "").trim()) || {}).pickupDate || "",
  ).trim();

  /** @type {Map<string, Map<string, { shell: object, orders: object[], unknownIds: string[] }>>} */
  const buckets = new Map();
  const ensure = (date, stop) => {
    const d = String(date || homeDate || "unknown").trim() || "unknown";
    if (!buckets.has(d)) buckets.set(d, new Map());
    const rests = buckets.get(d);
    const key = rid(stop.restaurantId) || d;
    if (!rests.has(key)) rests.set(key, { shell: stop, orders: [], unknownIds: [] });
    return rests.get(key);
  };

  let sawKnown = false;
  for (const stop of plan.stops) {
    const fallbackDate = String(stop.pickupDate || homeDate || "").trim();
    for (const id of getStopOrderIds(stop)) {
      const order = byId.get(id);
      if (!order) {
        ensure(fallbackDate, stop).unknownIds.push(id);
        continue;
      }
      sawKnown = true;
      if (!isDriveOrderRecord(order)) continue;
      const st = String(order.status || "").toLowerCase();
      if (st === "cancelled" || st === "expired" || st === "hold") continue;
      const date = String(order.pickupDate || fallbackDate || "").trim();
      ensure(date, stop).orders.push(order);
    }
  }

  if (!sawKnown) return { plan: plan, extraPlans: [], changed: false };

  const stopFromCluster = (shell, cluster, unknownIds = []) => {
    const group = cluster.length ? groupFromOrders(cluster, shell) : null;
    if (group && unknownIds.length) {
      const ids = [...new Set([...group.orderIds, ...unknownIds])];
      group.orderIds = ids;
      group.key = [...ids].sort().join("__");
    }
    const pickupGroups = group
      ? [group]
      : unknownIds.length
        ? [
            {
              key: unknownIds.join("__"),
              orderIds: unknownIds,
              tokenNumbers: [],
              status: "ordered",
              pickupWindow: shell.pickupWindow || null,
              pickupDate: shell.pickupDate || null,
            },
          ]
        : [];
    return buildStopFromGroups(shell, pickupGroups);
  };

  const extraPlans = [];
  const pushExtra = (date, stop) => {
    if (!stop) return;
    const ids = getStopOrderIds(stop);
    const safe = String(ids[0] || date || "trip").replace(/[^\w-]/g, "").slice(0, 40);
    const extra = {
      ...plan,
      id: `trip-${safe}`,
      stops: [stop],
      inProcess: false,
      tripStartedAt: null,
      parkedAt: plan.parkedAt || new Date().toISOString(),
    };
    delete extra.navigationOpenedAt;
    extraPlans.push(extra);
  };

  const dates = [...buckets.keys()];
  const home = buckets.has(homeDate) ? homeDate : dates[0];
  const homeStops = [];
  for (const date of dates) {
    const isHome = date === home;
    for (const { shell, orders: dayOrders, unknownIds } of (buckets.get(date) || new Map()).values()) {
      const clusters = clusterAddOnOrders(dayOrders);
      if (clusters.length <= 1) {
        const built = stopFromCluster(shell, clusters[0] || [], unknownIds);
        if (!built) continue;
        if (isHome) homeStops.push(built);
        else pushExtra(date, built);
        continue;
      }
      const primary = rid(shell.orderId);
      let keepIdx = clusters.findIndex((cluster) =>
        cluster.some((order) => rid(order._id) === primary),
      );
      if (keepIdx < 0) keepIdx = 0;
      clusters.forEach((cluster, index) => {
        const built = stopFromCluster(shell, cluster, index === keepIdx ? unknownIds : []);
        if (!built) return;
        if (isHome && index === keepIdx) homeStops.push(built);
        else pushExtra(date, built);
      });
    }
  }

  const nextPlan = homeStops.length ? { ...plan, stops: homeStops } : null;
  const changed = stopSignature(plan) !== stopSignature(nextPlan) || extraPlans.length > 0;
  return { plan: nextPlan, extraPlans, changed };
}

function tripOrderFingerprint(plan) {
  const ids = [];
  for (const stop of plan?.stops || []) {
    for (const id of getStopOrderIds(stop)) ids.push(id);
  }
  return ids.slice().sort().join(",");
}

/**
 * Keep add-more-items on their trip. Every other order becomes its own trip
 * with its own token, even when From and To match.
 * @returns {boolean} whether stored trips changed
 */
export function applyDayTripOrderSplit(orders) {
  const list = Array.isArray(orders) ? orders : [];
  let changed = false;

  const parkExtra = (extra) => {
    if (!extra?.stops?.length) return;
    const fp = tripOrderFingerprint(extra);
    if (!fp) return;
    const already =
      tripOrderFingerprint(loadDriveMultiStopPlan()) === fp ||
      loadCurrentDriveTrips().some((t) => tripOrderFingerprint(t) === fp);
    if (already) return;
    parkDriveTrip(extra);
    changed = true;
  };

  const active = loadDriveMultiStopPlan();
  if (active?.stops?.length) {
    const split = splitDriveTripOrdersByDay(active, list);
    if (split.changed) {
      if (stopSignature(active) !== stopSignature(split.plan)) {
        if (split.plan) saveDriveMultiStopPlan(split.plan);
        else clearDriveMultiStopPlan();
        changed = true;
      }
      for (const extra of split.extraPlans) parkExtra(extra);
    }
  }

  const parked = loadCurrentDriveTrips();
  let parkedChanged = false;
  const nextParked = [];
  for (const trip of parked) {
    if (isInProcessDriveTrip(trip)) {
      nextParked.push(trip);
      continue;
    }
    const split = splitDriveTripOrdersByDay(trip, list);
    if (!split.changed || stopSignature(trip) === stopSignature(split.plan)) {
      nextParked.push(trip);
      for (const extra of split.extraPlans || []) {
        if (tripOrderFingerprint(extra) === tripOrderFingerprint(trip)) continue;
        const dup = nextParked.some((t) => tripOrderFingerprint(t) === tripOrderFingerprint(extra));
        if (!dup) {
          nextParked.push({ ...extra, id: extra.id || trip.id });
          parkedChanged = true;
        }
      }
      continue;
    }
    parkedChanged = true;
    if (split.plan?.stops?.length) nextParked.push({ ...split.plan, id: trip.id });
    for (const extra of split.extraPlans) {
      const dup = nextParked.some((t) => tripOrderFingerprint(t) === tripOrderFingerprint(extra));
      if (!dup) nextParked.push(extra);
    }
  }
  if (parkedChanged) {
    saveCurrentDriveTrips(nextParked);
    changed = true;
  }
  return changed;
}

export function isDrivePlanComplete(plan) {
  if (!plan) return true;
  const stops = getOrderedStops(plan);
  if (!stops.length) return true;
  return (
    stops.every((s) => isPaidDriveStopStatus(s.status)) ||
    (stops.length >= MAX_DRIVE_STOPS && !getActiveOpenStop(plan))
  );
}

/** True when every paid restaurant on the trip has been collected. */
export function areAllDriveStopsTaken(plan) {
  const paid = getPaidStops(plan);
  if (!paid.length) return false;
  if (getActiveOpenStop(plan)) return false;
  return paid.every((s) => s.status === "taken");
}

/**
 * Earliest paid pickup window start on the trip.
 * Once this time arrives, the user can no longer add restaurants or more items.
 */
export function getEarliestPaidPickupAt(plan) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;
  let earliest = null;
  for (const stop of paid) {
    const start = slotStartFromStop(stop);
    if (start && (!earliest || start < earliest)) earliest = start;
  }
  return earliest;
}

/** True once now is at/after the earliest paid pickup window start. */
export function hasDrivePickupStarted(plan, now = new Date()) {
  const earliest = getEarliestPaidPickupAt(plan);
  if (!earliest || Number.isNaN(earliest.getTime())) return false;
  return now.getTime() >= earliest.getTime();
}

export function canAddAnotherDriveStop(plan, now = new Date()) {
  if (!DRIVE_MULTI_STOP_ENABLED) return false;
  if (!plan) return true;
  // One restaurant per trip. Extra items at that restaurant use Add more items.
  if (getPaidStops(plan).length >= 1) return false;
  // After the first pickup window starts, no more restaurants on this drive.
  if (hasDrivePickupStarted(plan, now)) return false;
  return true;
}

function slotEndIsoFromWindow(pickupWindow, pickupDate) {
  const parts = String(pickupWindow || "").split(/\s*-\s*/);
  const endHm = (parts[1] || parts[0] || "").trim();
  const hm = endHm.match(/^(\d{1,2}):(\d{2})/);
  const dp = String(pickupDate || "").split("/").map(Number);
  if (!hm || dp.length < 3) return null;
  const d = new Date(dp[2], dp[0] - 1, dp[1], Number(hm[1]), Number(hm[2]), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function slotStartFromStop(stop) {
  if (!stop) return null;
  return parsePickupSlotInstant(stop.pickupWindow, stop.pickupDate);
}

/**
 * Earliest "leave home" among paid stops. This is the pre-trip deadline:
 * before it, the user may still insert earlier restaurants on the route.
 */
export function getEarliestPaidLeaveHomeAt(plan) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;
  let earliest = null;
  for (const stop of paid) {
    let leave = null;
    if (stop.leaveByAt) {
      const d = new Date(stop.leaveByAt);
      if (!Number.isNaN(d.getTime())) leave = d;
    }
    // Fallback: derive leave ≈ slot start when leaveByAt was not stored (older plans).
    if (!leave) leave = slotStartFromStop(stop);
    if (leave && (!earliest || leave < earliest)) earliest = leave;
  }
  return earliest;
}

/** Human-readable leave-home label for the trip (earliest paid stop). */
export function getTripLeaveHomeLabel(plan) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;
  const earliest = getEarliestPaidLeaveHomeAt(plan);
  if (!earliest) {
    return paid.find((s) => s.leaveByLabel)?.leaveByLabel || null;
  }
  const match = paid.find((s) => {
    if (!s.leaveByAt) return false;
    const d = new Date(s.leaveByAt);
    return !Number.isNaN(d.getTime()) && Math.abs(d.getTime() - earliest.getTime()) < 2000;
  });
  if (match?.leaveByLabel) return match.leaveByLabel;
  try {
    return earliest.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

function placeLabel(point) {
  return String(point?.formattedAddress || point?.address || "").trim() || null;
}

/** Customer departure saved on the order, or still sitting on the checkout slot. */
function driveDeparturePoint(order, restaurantId) {
  const fromOrder = placeLabel({ formattedAddress: order?.driveOriginAddress });
  if (fromOrder) {
    const lat = Number(order?.driveOriginLat);
    const lng = Number(order?.driveOriginLng);
    return {
      formattedAddress: fromOrder,
      address: fromOrder,
      ...(Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : {}),
    };
  }
  const slot = readSmartRouteSlot(restaurantId);
  const fromSlot = slot?.restaurantDrive ? placeLabel(slot.driveOrigin) : "";
  if (!fromSlot) return null;
  const lat = Number(slot.driveOrigin?.lat);
  const lng = Number(slot.driveOrigin?.lng);
  return {
    formattedAddress: fromSlot,
    address: fromSlot,
    ...(Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : {}),
    ...(slot.driveOrigin?.placeId ? { placeId: slot.driveOrigin.placeId } : {}),
  };
}

/**
 * A recovered trip used the restaurant name as From when the typed address was lost.
 * Put the customer's departure back when we have it.
 */
function withCustomerDeparture(plan, departure, restaurantName) {
  if (!plan || !departure?.formattedAddress) return plan;
  const current = String(placeLabel(plan.origin) || "").trim().toLowerCase();
  const next = String(departure.formattedAddress).trim().toLowerCase();
  const name = String(restaurantName || "").trim().toLowerCase();
  const missingOrRestaurant = !current || (name && current === name);
  const differentStreet = Boolean(current) && !sameDriveAddress(current, next);
  // One restaurant: the street saved at checkout is the start, even after Near me mode is left.
  // A longer trip keeps its shared start unless Near me is still the mode that chose this street.
  const onlyThisPickup = getPaidStops(plan).length <= 1;
  const moved = differentStreet && (isNearMeExploreMode() || onlyThisPickup);
  if ((!missingOrRestaurant && !moved) || current === next) return plan;
  if (!moved) return { ...plan, origin: departure };
  return {
    ...plan,
    origin: departure,
    roadLegMinutes: [],
    routePolyline: [],
    routeDurationMinutes: null,
  };
}

/** Checkout drive time for this order, after a stale home route was dropped. */
function withCheckoutDriveTiming(plan, order) {
  if (!plan || !order) return plan;
  const orderId = String(order._id || order.orderId || "").trim();
  const snap = order.drivePickupTimingSnapshot || {};
  const driveMinutes = Math.round(Number(snap.driveMinutes));
  const leaveDate = order.driveLeaveHomeAt ? new Date(order.driveLeaveHomeAt) : null;
  const leaveByAt =
    leaveDate && !Number.isNaN(leaveDate.getTime()) ? leaveDate.toISOString() : null;
  const leaveByLabel = String(snap.leaveByLabel || "").trim() || null;
  if (!orderId || (!(driveMinutes > 0) && !leaveByAt && !leaveByLabel)) return plan;

  let changed = false;
  const stops = (plan.stops || []).map((stop) => {
    const ids = [stop?.orderId, ...(Array.isArray(stop?.previousOrderIds) ? stop.previousOrderIds : [])]
      .map((id) => String(id || ""))
      .filter(Boolean);
    if (!ids.includes(orderId)) return stop;
    const next = { ...stop };
    if (driveMinutes > 0 && next.driveMinutes !== driveMinutes) {
      next.driveMinutes = driveMinutes;
      changed = true;
    }
    if (leaveByAt && next.leaveByAt !== leaveByAt) {
      next.leaveByAt = leaveByAt;
      changed = true;
    }
    if (leaveByLabel && next.leaveByLabel !== leaveByLabel) {
      next.leaveByLabel = leaveByLabel;
      changed = true;
    }
    return next;
  });
  if (!changed) return plan;
  return {
    ...plan,
    stops,
    ...(leaveByAt ? { departureIso: leaveByAt } : {}),
    ...(leaveByLabel ? { leaveByLabel } : {}),
  };
}

function looseAddressKey(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\bfrance\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** From line on the order card. The street saved with the order is the one the customer chose. */
export function orderPlanDepartureLabel(plan, orderAddress) {
  const saved = String(orderAddress || "").trim();
  if (saved) return saved;
  return placeLabel(plan?.origin) || "";
}

/** Same street even when one line adds the country or the pin sits off the door. */
export function sameDriveAddress(a, b) {
  const left = looseAddressKey(a);
  const right = looseAddressKey(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const shorter = left.length <= right.length ? left : right;
  const longer = shorter === left ? right : left;
  if (!/\d/.test(shorter) || shorter.length < 8) return false;
  return longer.includes(shorter);
}

/**
 * Trip destination is this pickup restaurant (same id, coordinates, or address).
 * Arrival at that restaurant is the end of the trip, so End matches the pickup time.
 */
function destinationIsRestaurantStop(destination, stop) {
  if (!destination || !stop) return false;
  const destRid = rid(destination.restaurantId);
  const stopRid = rid(stop.restaurantId);
  if (destRid && stopRid && destRid === stopRid) return true;

  if (
    driveEndpointsMatch(destination, {
      lat: stop.lat,
      lng: stop.lng,
      formattedAddress: stop.address,
      address: stop.address,
    })
  ) {
    return true;
  }

  const destLabel = placeLabel(destination) || "";
  return (
    sameDriveAddress(destLabel, stop.address) ||
    sameDriveAddress(destLabel, stop.name)
  );
}

function formatTripClockLabel(date) {
  if (!date || Number.isNaN(date.getTime())) return null;
  try {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
}

/** Slot end Date from a paid stop (window end, else start). */
function slotEndFromStop(stop) {
  if (!stop) return null;
  if (stop.slotEndIso) {
    const d = new Date(stop.slotEndIso);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const start = slotStartFromStop(stop);
  if (!start) return null;
  const parts = String(stop.pickupWindow || "").split(/\s*-\s*/);
  const hm = String(parts[1] || "").match(/^(\d{1,2}):(\d{2})/);
  if (hm && stop.pickupDate) {
    const dp = String(stop.pickupDate).split("/").map(Number);
    if (dp.length >= 3) {
      const end = new Date(dp[2], dp[0] - 1, dp[1], Number(hm[1]), Number(hm[2]), 0, 0);
      if (!Number.isNaN(end.getTime())) return end;
    }
  }
  return start;
}

/**
 * Origin → first restaurant drive time (same basis as shop cards).
 * Prefer stored driveMinutes / leave→slot, not along-route routeOffsetMinutes.
 */
function originToFirstStopDriveMinutes(firstStop, leaveAt) {
  if (!firstStop) return 0;
  const stored = Number(firstStop.driveMinutes);
  if (Number.isFinite(stored) && stored > 0) return Math.round(stored);

  const slotStart = slotStartFromStop(firstStop);
  if (leaveAt && slotStart) {
    const derived = Math.round((slotStart.getTime() - leaveAt.getTime()) / 60000);
    if (derived > 0) return derived;
  }

  const leaveIso = firstStop.leaveByAt ? new Date(firstStop.leaveByAt) : null;
  if (leaveIso && !Number.isNaN(leaveIso.getTime()) && slotStart) {
    const derived = Math.round((slotStart.getTime() - leaveIso.getTime()) / 60000);
    if (derived > 0) return derived;
  }

  return Math.max(0, Math.round(Number(firstStop.routeOffsetMinutes) || 0));
}

/** Google leg minutes, or [] when any value is missing. */
function roadLegMinutesOf(plan) {
  const raw = Array.isArray(plan?.roadLegMinutes) ? plan.roadLegMinutes : [];
  if (!raw.length) return [];
  const minutes = raw.map((n) => Math.round(Number(n)));
  if (minutes.some((n) => !Number.isFinite(n) || n <= 0)) return [];
  return minutes;
}

function expectedRoadLegCount(plan, paid, endsAtRestaurant) {
  const destLeg = !endsAtRestaurant && Boolean(placeLabel(plan?.destination));
  return paid.length + (destLeg ? 1 : 0);
}

/**
 * Trip itinerary for the plan bar after orders are placed.
 * Includes drive-leg ETAs: origin → stop1 → … → destination.
 */
export function getDriveTripItinerary(plan) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;
  const leaveAt = getEarliestPaidLeaveHomeAt(plan);
  const pickups = paid.map((s, i) => ({
    index: i + 1,
    restaurantId: s.restaurantId,
    name: s.name,
    pickupWindow: s.pickupWindow,
    pickupDate: s.pickupDate,
    leaveByLabel: s.leaveByLabel,
    driveMinutes: Number.isFinite(Number(s.driveMinutes)) ? Number(s.driveMinutes) : null,
    address: s.address || null,
    routeOffsetMinutes: Number(s.routeOffsetMinutes) || 0,
    orderId: s.orderId || null,
    previousOrderIds: Array.isArray(s.previousOrderIds) ? s.previousOrderIds : [],
    status: s.status || "ordered",
    takenAt: s.takenAt || null,
  }));

  const routeDuration = Number(plan?.routeDurationMinutes);
  const lastPaid = paid[paid.length - 1];
  const endsAtRestaurant = driveTripEndsAtRestaurant(plan);
  const roadLegs = roadLegMinutesOf(plan);
  const useRoadLegs = roadLegs.length === expectedRoadLegCount(plan, paid, endsAtRestaurant);
  const legs = [];
  for (let i = 0; i < pickups.length; i++) {
    let minutes;
    if (useRoadLegs) {
      // Live Google drive for this hop. The stored driveMinutes value is a
      // straight-line estimate and runs short of Maps.
      minutes = roadLegs[i];
    } else if (i === 0) {
      // Match restaurant card: real drive from departure, not corridor route fraction.
      minutes = originToFirstStopDriveMinutes(paid[0], leaveAt);
      // A saved second hop is the restaurant street treated as another stop.
      // That total is not the drive to the pickup.
      const extraDestinationHop =
        endsAtRestaurant && roadLegs.length > expectedRoadLegCount(plan, paid, true);
      if (extraDestinationHop) {
        minutes = roadLegs[0];
      } else if (
        pickups.length === 1 &&
        endsAtRestaurant &&
        Number.isFinite(routeDuration) &&
        routeDuration > 0
      ) {
        // One pickup that is the destination: Maps shows a single drive to that address.
        // Prefer that road time over the shorter straight-line estimate.
        minutes = Math.round(routeDuration);
      }
    } else {
      minutes = Math.max(
        0,
        Math.round(pickups[i].routeOffsetMinutes - pickups[i - 1].routeOffsetMinutes),
      );
    }
    legs.push({ afterIndex: i - 1, minutes }); // afterIndex -1 = after origin
  }
  let finalLegMinutes = null;
  if (!endsAtRestaurant && (useRoadLegs || (Number.isFinite(routeDuration) && routeDuration > 0))) {
    if (useRoadLegs) {
      finalLegMinutes = roadLegs[roadLegs.length - 1];
    } else {
      const lastOffset = pickups[pickups.length - 1].routeOffsetMinutes;
      finalLegMinutes = Math.max(0, Math.round(routeDuration - lastOffset));
    }
    legs.push({
      afterIndex: pickups.length - 1,
      minutes: finalLegMinutes,
      toDestination: true,
    });
  }

  // Arrival at destination ≈ leave last pickup (slot end + dwell) + final drive leg.
  // When the destination is that restaurant, the driver is already there at pickup time.
  let endByAt = null;
  let endByLabel = null;
  if (endsAtRestaurant) {
    finalLegMinutes = 0;
  }
  const lastSlotEnd = slotEndFromStop(lastPaid);
  if (lastSlotEnd) {
    const afterLastMinutes = endsAtRestaurant
      ? 0
      : DRIVE_STOP_DWELL_MINUTES + (Number.isFinite(finalLegMinutes) ? finalLegMinutes : 0);
    endByAt = new Date(lastSlotEnd.getTime() + afterLastMinutes * 60 * 1000);
    endByLabel = formatTripClockLabel(endByAt);
  } else if (leaveAt && Number.isFinite(routeDuration) && routeDuration > 0) {
    const dwellMinutes = endsAtRestaurant ? 0 : pickups.length * DRIVE_STOP_DWELL_MINUTES;
    endByAt = new Date(leaveAt.getTime() + (routeDuration + dwellMinutes) * 60 * 1000);
    endByLabel = formatTripClockLabel(endByAt);
  }

  // Leave home so the driver reaches the first restaurant at the pickup time.
  // A search clock ("leave now") is earlier than that and must not replace it.
  let leaveByAtIso = leaveAt ? leaveAt.toISOString() : null;
  let leaveByLabel = getTripLeaveHomeLabel(plan);
  const legTotal = legs.reduce((sum, leg) => sum + (Number(leg.minutes) || 0), 0);
  const displayedRouteMinutes = useRoadLegs
    ? roadLegs.reduce((sum, minutes) => sum + minutes, 0)
    : Number.isFinite(routeDuration) && routeDuration > 0
      ? routeDuration
      : legTotal;
  if (useRoadLegs) {
    const firstSlotEnd = slotEndFromStop(paid[0]);
    if (firstSlotEnd) {
      const googleLeave = new Date(firstSlotEnd.getTime() - roadLegs[0] * 60 * 1000);
      if (!Number.isNaN(googleLeave.getTime())) {
        leaveByAtIso = googleLeave.toISOString();
        leaveByLabel = formatTravelTimeLabel(googleLeave);
      }
    }
  }

  return {
    fromLabel: placeLabel(plan?.origin),
    toLabel: endsAtRestaurant ? "" : placeLabel(plan?.destination),
    leaveByAt: leaveByAtIso,
    leaveByLabel,
    endByAt: endByAt ? endByAt.toISOString() : null,
    endByLabel,
    pickups,
    legs,
    routeDurationMinutes: Number.isFinite(displayedRouteMinutes) ? displayedRouteMinutes : null,
    tripStartedAt: plan?.tripStartedAt || null,
  };
}

/**
 * Keep Google hop times on the plan. The first pickup's drive minutes and
 * leave clock follow the origin → restaurant road time.
 */
export function applyRoadLegsToPlan(plan, roadLegMinutes) {
  const paid = getPaidStops(plan);
  if (!plan || !paid.length) return plan;
  const minutes = roadLegMinutesOf({ roadLegMinutes });
  const endsAtRestaurant = driveTripEndsAtRestaurant(plan);
  if (!minutes.length || minutes.length !== expectedRoadLegCount(plan, paid, endsAtRestaurant)) {
    return plan;
  }

  const first = paid[0];
  const firstSlotEnd = slotEndFromStop(first);
  const googleLeave = firstSlotEnd
    ? new Date(firstSlotEnd.getTime() - minutes[0] * 60 * 1000)
    : null;
  const stops = (plan.stops || []).map((stop) => {
    const sameStop =
      rid(stop.restaurantId) === rid(first.restaurantId) &&
      String(stop.orderId || "") === String(first.orderId || "") &&
      String(stop.pickupWindow || "") === String(first.pickupWindow || "");
    if (!sameStop) return stop;
    const next = { ...stop, driveMinutes: minutes[0] };
    if (googleLeave && !Number.isNaN(googleLeave.getTime())) {
      next.leaveByAt = googleLeave.toISOString();
      next.leaveByLabel = formatTravelTimeLabel(googleLeave);
    }
    return next;
  });

  const sameLegs =
    roadLegMinutesOf(plan).length === minutes.length &&
    roadLegMinutesOf(plan).every((value, index) => value === minutes[index]);
  const sameStops = (plan.stops || []).every((stop, index) => {
    const next = stops[index];
    return stop.driveMinutes === next.driveMinutes && stop.leaveByAt === next.leaveByAt && stop.leaveByLabel === next.leaveByLabel;
  });
  if (sameLegs && sameStops && (!endsAtRestaurant || Number(plan.routeDurationMinutes) === minutes[0])) {
    return plan;
  }

  return {
    ...plan,
    stops,
    roadLegMinutes: minutes,
    ...(endsAtRestaurant ? { routeDurationMinutes: minutes[0] } : {}),
  };
}

/** Mongo order ids for all paid stops (incl. same-stop add-ons). */
export function getPaidDriveOrderIds(plan) {
  const ids = [];
  for (const s of getPaidStops(plan)) {
    if (s.orderId) ids.push(String(s.orderId));
    if (Array.isArray(s.previousOrderIds)) {
      for (const id of s.previousOrderIds) {
        if (id) ids.push(String(id));
      }
    }
  }
  return [...new Set(ids)];
}

/**
 * Find the multi-stop Drive trip that includes this paid order.
 * Checks active plan → parked current trips → closed history.
 * @returns {{ plan: object, source: 'active'|'parked'|'history', parkedTripId: string|null, readOnly: boolean } | null}
 */
export function findDriveTripPlanForOrderId(orderId) {
  const oid = String(orderId || "").trim();
  if (!oid) return null;

  const planHasOrder = (plan) => {
    if (!plan) return false;
    if (getPaidDriveOrderIds(plan).includes(oid)) return true;
    return getOrderedStops(plan).some((s) => getStopOrderIds(s).includes(oid));
  };

  const active = loadDriveMultiStopPlan();
  if (planHasOrder(active)) {
    return {
      plan: active,
      source: "active",
      parkedTripId: null,
      readOnly: false,
    };
  }

  for (const trip of loadCurrentDriveTrips()) {
    if (isInProcessDriveTrip(trip)) continue;
    if (!planHasOrder(trip)) continue;
    return {
      plan: trip,
      source: "parked",
      parkedTripId: trip.id ? String(trip.id) : null,
      readOnly: false,
    };
  }

  for (const trip of loadDriveTripHistory()) {
    const ids = [];
    for (const s of trip.stops || []) {
      for (const id of getStopOrderIds(s)) ids.push(id);
    }
    if (!ids.includes(oid)) continue;
    const plan = historyTripToPlan(trip);
    if (!plan) continue;
    return {
      plan,
      source: "history",
      parkedTripId: null,
      readOnly: true,
    };
  }

  return null;
}

/** Mark that the user tapped Start my trip (keeps plan for reference). */
export function markDriveTripStarted(plan = loadDriveMultiStopPlan()) {
  if (!plan) return null;
  const next = { ...plan, tripStartedAt: new Date().toISOString() };
  return saveDriveMultiStopPlan(next);
}

/**
 * True once the drive has effectively started: now is at/after the earliest paid leave-home.
 * Example: first order leave home 19:54 → until then, book earlier restaurants; after 19:54,
 * only further-along stops remain allowed.
 */
export function hasDriveTripStarted(plan, now = new Date()) {
  const earliest = getEarliestPaidLeaveHomeAt(plan);
  if (!earliest || Number.isNaN(earliest.getTime())) return false;
  return now.getTime() >= earliest.getTime();
}

/** While still planning (trip not started), allow any unbooked restaurant on the route. */
export function mustPickFurtherAlongOnly(plan, now = new Date()) {
  return hasDriveTripStarted(plan, now);
}

/**
 * User already paid at this stop but can still place another order for more items
 * until that stop’s pickup window starts.
 */
export function canAddMoreItemsAtDriveStop(plan, restaurantId, now = new Date()) {
  // Same-stop add-ons stay available even when multi-restaurant stops are off.
  const stop = getOrderedStops(plan).find((s) => rid(s.restaurantId) === rid(restaurantId));
  if (!stop || stop.status !== "ordered") return false;
  const start = slotStartFromStop(stop);
  if (start && !Number.isNaN(start.getTime())) {
    return now.getTime() < start.getTime();
  }
  // No slot timing → allow until the overall drive has started.
  return !hasDriveTripStarted(plan, now);
}

/** Update one add-on group on a stop. Other orders on the same day stay separate. */
function patchPickupGroupStatus(stop, orderId, status, opts = {}) {
  const groups = Array.isArray(stop?.pickupGroups) ? stop.pickupGroups : null;
  if (!groups?.length) return stop;
  const oid = rid(orderId);
  const completed = opts.completedIds instanceof Set ? opts.completedIds : null;
  let touched = false;
  const pickupGroups = groups.map((g) => {
    const ids = (Array.isArray(g.orderIds) ? g.orderIds : []).map((id) => rid(id));
    const hit = ids.includes(oid) || (completed && ids.some((id) => completed.has(id) && id === oid));
    if (!hit && !(completed && ids.includes(oid))) return g;
    if (!ids.includes(oid)) return g;
    touched = true;
    const groupDone =
      status === "taken" &&
      completed &&
      ids.every((id) => completed.has(id) || id === oid);
    return {
      ...g,
      status: status === "taken" ? (groupDone || ids.length <= 1 ? "taken" : g.status) : status,
    };
  });
  if (!touched) return stop;
  const allTaken = pickupGroups.every((g) => g.status === "taken");
  const anyReady = pickupGroups.some((g) => g.status === "ready");
  return {
    ...stop,
    pickupGroups,
    status: allTaken ? "taken" : anyReady ? "ready" : stop.status === "ready" ? "ordered" : stop.status,
  };
}

/**
 * Mark that the restaurant notified the customer the order is ready to pick up.
 * Only upgrades stops that are still "ordered" (not yet taken).
 *
 * @param {string} orderId
 * @param {{ readyAt?: string|null }} [opts]
 * @returns {{ plan: object|null, stop: object|null, changed: boolean }}
 */
export function markDriveOrderReady(orderId, opts = {}) {
  const oid = String(orderId || "").trim();
  if (!oid) {
    return { plan: null, stop: null, changed: false };
  }

  const readyAt =
    opts.readyAt ||
    opts.readyNotificationSentAt ||
    new Date().toISOString();

  const plan = loadDriveMultiStopPlan();
  if (!plan?.stops?.length) {
    const parked = markCurrentDriveTripOrderReady(oid, { ...opts, readyAt });
    return {
      plan: parked.plan,
      stop: parked.stop,
      changed: parked.changed,
    };
  }

  let matchedStop = null;
  let changed = false;

  const stops = getOrderedStops(plan).map((s) => {
    const stopIds = getStopOrderIds(s);
    if (!stopIds.includes(oid)) return s;
    if (s.status === "taken") {
      matchedStop = matchedStop || s;
      return s;
    }

    const grouped = patchPickupGroupStatus(s, oid, "ready");
    if (s.status === "ready" && grouped === s) {
      matchedStop = s;
      if (!s.readyAt && readyAt) {
        changed = true;
        matchedStop = { ...s, readyAt, orderStatus: "ready" };
        return matchedStop;
      }
      return s;
    }
    if (s.status === "ready" && grouped !== s) {
      changed = true;
      matchedStop = { ...grouped, readyAt: s.readyAt || readyAt, orderStatus: "ready" };
      return matchedStop;
    }
    if (s.status !== "ordered" && s.status !== "open") return s;

    changed = true;
    matchedStop = {
      ...grouped,
      status: grouped.pickupGroups?.length ? grouped.status : "ready",
      orderStatus: "ready",
      readyAt,
    };
    return matchedStop;
  });

  if (!matchedStop) {
    // Order belongs to a parked Current trip, not the active plan.
    const parked = markCurrentDriveTripOrderReady(oid, { ...opts, readyAt });
    if (parked.changed || parked.plan) {
      return {
        plan: parked.plan,
        stop: parked.stop,
        changed: parked.changed,
      };
    }
    return { plan, stop: null, changed: false };
  }

  if (!changed) {
    return { plan, stop: matchedStop, changed: false };
  }

  const next = saveDriveMultiStopPlan({ ...plan, stops });
  return { plan: next, stop: matchedStop, changed: true };
}

/**
 * Mark that the user collected an order belonging to the active multi-stop plan.
 * When every paid restaurant is taken, archive the trip to history and clear the plan.
 *
 * @param {string} orderId
 * @param {{ completedOrderIds?: string[] }} [opts] — optional known-completed order ids
 *   (e.g. from the orders list) so same-stop add-ons can all count as taken together.
 * @returns {{ plan: object|null, stop: object|null, tripClosed: boolean, archivedTrip: object|null }}
 */
export function markDriveOrderTaken(orderId, opts = {}) {
  const oid = String(orderId || "").trim();
  if (!oid) {
    return { plan: null, stop: null, tripClosed: false, archivedTrip: null };
  }

  const plan = loadDriveMultiStopPlan();
  if (!plan) {
    const parked = markCurrentDriveTripOrderTaken(oid, opts);
    return {
      plan: parked.plan,
      stop: null,
      tripClosed: parked.tripClosed,
      archivedTrip: parked.archivedTrip,
    };
  }

  const completedSet = new Set(
    (Array.isArray(opts.completedOrderIds) ? opts.completedOrderIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean),
  );
  completedSet.add(oid);

  const takenAt = new Date().toISOString();
  let matchedStop = null;

  const stops = getOrderedStops(plan).map((s) => {
    const stopIds = getStopOrderIds(s);
    if (!stopIds.includes(oid)) return s;
    if (s.status === "taken") {
      matchedStop = s;
      return s;
    }

    const takenSoFar = new Set([
      ...(Array.isArray(s.takenOrderIds) ? s.takenOrderIds.map(String) : []),
      ...completedSet,
    ]);
    const allTaken = stopIds.every((id) => takenSoFar.has(id));
    const nextTakenIds = [...new Set([...takenSoFar].filter((id) => stopIds.includes(id)))];

    const grouped = patchPickupGroupStatus(s, oid, "taken", { completedIds: completedSet });
    if (!allTaken) {
      matchedStop = {
        ...grouped,
        takenOrderIds: nextTakenIds,
      };
      return matchedStop;
    }
    matchedStop = {
      ...grouped,
      status: "taken",
      takenAt,
      takenOrderIds: stopIds,
    };
    return matchedStop;
  });

  if (!matchedStop) {
    // Order belongs to another concurrent current trip
    const parked = markCurrentDriveTripOrderTaken(oid, opts);
    if (parked.plan) {
      return {
        plan: parked.plan,
        stop: null,
        tripClosed: parked.tripClosed,
        archivedTrip: parked.archivedTrip,
      };
    }
    return { plan, stop: null, tripClosed: false, archivedTrip: null };
  }

  plan.stops = stops;
  // Snapshot leave label for history before clear.
  if (!plan.leaveByLabel) {
    plan.leaveByLabel = getTripLeaveHomeLabel(plan);
  }
  saveDriveMultiStopPlan(plan);

  if (!areAllDriveStopsTaken(plan)) {
    return { plan, stop: matchedStop, tripClosed: false, archivedTrip: null };
  }

  const archivedTrip = archiveDriveTripToHistory(plan, { closedAt: takenAt });
  clearDriveMultiStopPlan();
  notifyTripHistoryUi();
  return { plan: null, stop: matchedStop, tripClosed: true, archivedTrip };
}

/**
 * Same From and To does not combine two planned trips.
 * A second token belongs on a trip only when the user adds articles
 * from "add more items". Another trip keeps its own token.
 */
export function consolidateSameCorridorDriveTrips() {
  const active = loadDriveMultiStopPlan();
  return { changed: false, plan: active || null };
}

function clockLabel(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Leave time for a moved pickup: arrive at the slot end, same drive minutes as before. */
function leaveForMovedPickup(stop, order) {
  const endIso = slotEndIsoFromWindow(order.pickupWindow, order.pickupDate);
  if (!endIso) return null;
  let drive = Math.max(
    0,
    Number(order.drivePickupTimingSnapshot?.driveMinutes) || Number(stop.driveMinutes) || 0,
  );
  if (!drive && stop.leaveByAt && stop.pickupWindow) {
    const oldEnd = slotEndIsoFromWindow(stop.pickupWindow, stop.pickupDate);
    if (oldEnd) {
      drive = Math.max(0, Math.round((new Date(oldEnd).getTime() - new Date(stop.leaveByAt).getTime()) / 60000));
    }
  }
  const leave = new Date(new Date(endIso).getTime() - drive * 60 * 1000);
  if (Number.isNaN(leave.getTime())) return null;
  const snap = order.drivePickupTimingSnapshot || {};
  const snapMatches = snap.slotLabel && String(snap.slotLabel) === String(order.pickupWindow);
  const serverLeave = order.driveLeaveHomeAt ? new Date(order.driveLeaveHomeAt) : null;
  const serverMatches =
    snapMatches &&
    serverLeave &&
    !Number.isNaN(serverLeave.getTime()) &&
    Math.abs(serverLeave.getTime() - leave.getTime()) < 2 * 60 * 1000;
  return {
    leaveByAt: serverMatches ? serverLeave.toISOString() : leave.toISOString(),
    leaveByLabel: serverMatches && snap.leaveByLabel ? snap.leaveByLabel : clockLabel(leave),
    driveMinutes: drive || stop.driveMinutes || null,
  };
}

function applyOrderPickupToPlan(plan, orders) {
  if (!plan?.stops?.length) return { plan, changed: false };
  const byId = new Map();
  for (const order of orders) {
    if (order?._id && order.pickupWindow && order.pickupDate) byId.set(String(order._id), order);
  }
  if (!byId.size) return { plan, changed: false };

  let changed = false;
  const stops = getOrderedStops(plan).map((stop) => {
    const order = getStopOrderIds(stop).map((id) => byId.get(id)).find(Boolean);
    if (!order) return stop;
    const sameSlot = order.pickupWindow === stop.pickupWindow && order.pickupDate === stop.pickupDate;
    const timing = leaveForMovedPickup(stop, order);
    if (!timing) return stop;
    const sameLeave = stop.leaveByAt && Math.abs(new Date(stop.leaveByAt).getTime() - new Date(timing.leaveByAt).getTime()) < 60 * 1000;
    if (sameSlot && sameLeave) return stop;
    changed = true;
    return {
      ...stop,
      pickupWindow: order.pickupWindow,
      pickupDate: order.pickupDate,
      leaveByAt: timing.leaveByAt,
      leaveByLabel: timing.leaveByLabel,
      driveMinutes: timing.driveMinutes,
      slotEndIso: slotEndIsoFromWindow(order.pickupWindow, order.pickupDate),
    };
  });
  if (!changed) return { plan, changed: false };
  const next = { ...plan, stops };
  next.leaveByLabel = getTripLeaveHomeLabel(next);
  const earliest = getEarliestPaidLeaveHomeAt(next);
  if (earliest) next.departureIso = earliest.toISOString();
  return { plan: next, changed: true };
}

function restaurantPlaceFromOrder(order) {
  const address = String(
    order?.restaurantAddress || order?.restaurant?.shopDetails?.address || "",
  ).trim();
  const lat = Number(order?.restaurantLocation?.lat);
  const lng = Number(order?.restaurantLocation?.lng);
  return {
    address: address || null,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
}

/** Paid stops created from the order often have only the restaurant name. */
function fillStopPlacesFromOrders(plan, orders) {
  if (!plan?.stops?.length) return { plan, changed: false };
  const byRestaurant = new Map();
  for (const order of orders || []) {
    const id = rid(order?.restaurantId);
    if (!id || byRestaurant.has(id)) continue;
    const place = restaurantPlaceFromOrder(order);
    if (place.address || place.lat != null) byRestaurant.set(id, place);
  }
  let changed = false;
  const stops = plan.stops.map((stop) => {
    const place = byRestaurant.get(rid(stop.restaurantId));
    if (!place) return stop;
    const next = { ...stop };
    let stopChanged = false;
    if (!String(stop.address || "").trim() && place.address) {
      next.address = place.address;
      stopChanged = true;
    }
    if (place.lat != null && !Number.isFinite(Number(stop.lat))) {
      next.lat = place.lat;
      next.lng = place.lng;
      stopChanged = true;
    }
    if (!stopChanged) return stop;
    changed = true;
    return next;
  });
  return changed ? { plan: { ...plan, stops }, changed: true } : { plan, changed: false };
}

/** Copy the restaurant street from each order onto a stop that only has the shop name. */
export function stampDriveStopPlacesFromOrders(orders) {
  let changed = false;
  const active = loadDriveMultiStopPlan();
  if (active) {
    const placed = fillStopPlacesFromOrders(active, orders);
    if (placed.changed) {
      saveDriveMultiStopPlan(placed.plan);
      changed = true;
    }
  }
  const parked = loadCurrentDriveTrips();
  let parkedChanged = false;
  const nextParked = parked.map((trip) => {
    const placed = fillStopPlacesFromOrders(trip, orders);
    if (!placed.changed) return trip;
    parkedChanged = true;
    return placed.plan;
  });
  if (parkedChanged) {
    saveCurrentDriveTrips(nextParked);
    changed = true;
  }
  return changed;
}

function syncStoredDrivePlansWithOrders(orders) {
  let changed = false;
  const active = loadDriveMultiStopPlan();
  if (active) {
    const placed = fillStopPlacesFromOrders(active, orders);
    const applied = applyOrderPickupToPlan(placed.plan, orders);
    if (placed.changed || applied.changed) {
      saveDriveMultiStopPlan(applied.changed ? applied.plan : placed.plan);
      changed = true;
    }
  }
  const parked = loadCurrentDriveTrips();
  let parkedChanged = false;
  const nextParked = parked.map((trip) => {
    const placed = fillStopPlacesFromOrders(trip, orders);
    const applied = applyOrderPickupToPlan(placed.plan, orders);
    if (!placed.changed && !applied.changed) return trip;
    parkedChanged = true;
    return applied.changed ? applied.plan : placed.plan;
  });
  if (parkedChanged) {
    saveCurrentDriveTrips(nextParked);
    changed = true;
  }
  return changed;
}

/**
 * Sync local trip plan with server order statuses.
 * Marks paid stops as taken when their orders are completed, as ready when the
 * restaurant has notified pickup, and drops a leftover open stop when that
 * restaurant's linked order is already collected (covers payment that never
 * called markDriveStopOrdered).
 *
 * @param {Array<{ _id?: string, status?: string, restaurantId?: string, orderType?: string, driveLeaveHomeAt?: string, readyAt?: string, readyNotificationSentAt?: string }>} orders
 * @returns {{ plan: object|null, changed: boolean, tripClosed: boolean }}
 */
export function reconcileDrivePlanWithOrders(orders) {
  const list = Array.isArray(orders) ? orders : [];
  const completedIds = list
    .filter((o) => String(o?.status || "").toLowerCase() === "completed" && o?._id)
    .map((o) => String(o._id));

  let plan = loadDriveMultiStopPlan();
  let changed = false;
  let tripClosed = false;

  // Always mark completed orders on active plan AND parked current trips.
  if (completedIds.length) {
    for (const oid of completedIds) {
      const beforePlan = loadDriveMultiStopPlan();
      const beforeCurrentCount = loadCurrentDriveTrips().length;
      const result = markDriveOrderTaken(oid, { completedOrderIds: completedIds });
      if (result.tripClosed) {
        tripClosed = true;
        changed = true;
      }
      const afterPlan = loadDriveMultiStopPlan();
      const afterCurrentCount = loadCurrentDriveTrips().length;
      if (
        result.stop ||
        afterPlan !== beforePlan ||
        afterCurrentCount !== beforeCurrentCount
      ) {
        changed = true;
      }
    }
  }

  plan = loadDriveMultiStopPlan();
  if (plan?.stops?.length) {
    // Restaurant marked ready to pick up — surface on the trip plan.
    const readyOrders = list.filter(
      (o) => String(o?.status || "").toLowerCase() === "ready" && o?._id,
    );
    for (const o of readyOrders) {
      const result = markDriveOrderReady(String(o._id), {
        readyAt: o.readyNotificationSentAt || o.readyAt || null,
        readyNotificationSentAt: o.readyNotificationSentAt || null,
      });
      if (result.changed) {
        changed = true;
        plan = result.plan;
      }
    }

    plan = loadDriveMultiStopPlan();
    if (plan?.stops?.length) {
      // Leftover "open" stop after the order was already collected: no orderId on the
      // stop, so markDriveOrderTaken never matched. Drop opens whose restaurant already
      // has a completed Drive order created during this plan window.
      const planSavedAt = Number(plan.savedAt) || 0;
      const completedByRestaurant = new Map();
      for (const o of list) {
        if (String(o?.status || "").toLowerCase() !== "completed") continue;
        const isDrive =
          String(o?.orderType || "").toUpperCase() === "DRIVE" ||
          Boolean(o?.driveLeaveHomeAt) ||
          Boolean(o?.drivePickupTimingSnapshot);
        if (!isDrive) continue;
        const restaurantId = rid(
          o.restaurantId || o.restaurant?._id || o.restaurant?.ownerId || o.ownerId,
        );
        if (!restaurantId) continue;
        const createdAt = o.createdAt ? new Date(o.createdAt).getTime() : 0;
        // Only orders placed during (or just before) this plan — not older trips.
        if (planSavedAt && createdAt && createdAt < planSavedAt - 2 * 60 * 1000) continue;
        completedByRestaurant.set(restaurantId, String(o._id || ""));
      }

      const stops = getOrderedStops(plan);
      const nextStops = stops.filter((s) => {
        if (s.status !== "open") return true;
        if (s.orderId) return true;
        return !completedByRestaurant.has(rid(s.restaurantId));
      });

      if (nextStops.length !== stops.length) {
        changed = true;
        if (!nextStops.length) {
          // Nothing left to show — if we had paid/taken stops they'd already be handled;
          // open-only leftovers just clear.
          clearDriveMultiStopPlan();
          notifyTripHistoryUi();
          // Still try to recover other open Drive orders below.
          plan = null;
        } else {
          plan = saveDriveMultiStopPlan({ ...plan, stops: nextStops });
        }
      }
    }
  }

  // Recover orphan paid Drive orders (e.g. online payment return never attached the stop).
  // History-only matches still count as orphans when the order is open on the server.
  for (const o of list) {
    if (!isOpenDriveOrderForTrip(o)) continue;
    const oid = String(o?._id || "").trim();
    if (!oid) continue;
    const found = findDriveTripPlanForOrderId(oid);
    if (found && found.source !== "history") continue;
    const attached = attachPaidDriveOrderToCurrentTrips(o);
    if (attached?.attached && !attached?.alreadyAttached) {
      changed = true;
      plan = attached.plan || loadDriveMultiStopPlan();
    }
  }

  // Same From/To stays two trips. Only add-more-items shares a token.
  const consolidated = consolidateSameCorridorDriveTrips();
  if (consolidated.changed) {
    changed = true;
    plan = consolidated.plan || loadDriveMultiStopPlan();
  }

  // Add-more-items stay on their trip. Every other order becomes its own trip.
  if (applyDayTripOrderSplit(list)) {
    changed = true;
    plan = loadDriveMultiStopPlan();
  }

  if (syncStoredDrivePlansWithOrders(list)) {
    changed = true;
    plan = loadDriveMultiStopPlan();
  }

  return { plan: plan || loadDriveMultiStopPlan(), changed, tripClosed };
}

/**
 * Earliest time for a stop at nextRouteOffsetMinutes, based on the previous paid stop
 * along the route (not booking order) + dwell + leg.
 */
export function computeNextStopNotBeforeIso(plan, nextRouteOffsetMinutes) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;

  const nextOffset = Number(nextRouteOffsetMinutes) || 0;
  const earlierAlong = paid.filter(
    (s) => (Number(s.routeOffsetMinutes) || 0) + 0.5 < nextOffset,
  );
  if (!earlierAlong.length) return null;

  const prev = earlierAlong[earlierAlong.length - 1];
  let prevEnd = null;
  if (prev.slotEndIso) {
    const d = new Date(prev.slotEndIso);
    if (!Number.isNaN(d.getTime())) prevEnd = d;
  }
  if (!prevEnd) {
    const prevStart = slotStartFromStop(prev);
    prevEnd = prevStart;
    if (prevStart && prev.pickupWindow) {
      const parts = String(prev.pickupWindow).split(/\s*-\s*/);
      const hm = String(parts[1] || "").match(/^(\d{1,2}):(\d{2})/);
      if (hm && prev.pickupDate) {
        const dp = String(prev.pickupDate).split("/").map(Number);
        if (dp.length >= 3) {
          prevEnd = new Date(dp[2], dp[0] - 1, dp[1], Number(hm[1]), Number(hm[2]), 0, 0);
        }
      }
    }
  }
  if (!prevEnd || Number.isNaN(prevEnd.getTime())) return null;

  const prevOffset = Number(prev.routeOffsetMinutes) || 0;
  const legMin = Math.max(0, nextOffset - prevOffset);
  const notBefore = new Date(
    prevEnd.getTime() + (DRIVE_STOP_DWELL_MINUTES + legMin) * 60 * 1000,
  );
  return notBefore.toISOString();
}

/**
 * Latest time a stop should finish so the driver can still reach the next paid pickup further along.
 * Used when inserting a restaurant earlier on the route while the trip has not started
 * (i.e. before the later stop’s leave-home time).
 */
export function computeNextStopNotAfterIso(plan, nextRouteOffsetMinutes) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;

  const nextOffset = Number(nextRouteOffsetMinutes) || 0;
  const laterAlong = paid.filter((s) => (Number(s.routeOffsetMinutes) || 0) > nextOffset + 0.5);
  if (!laterAlong.length) return null;

  const next = laterAlong[0];
  const nextStart = slotStartFromStop(next);
  if (!nextStart || Number.isNaN(nextStart.getTime())) return null;

  const legMin = Math.max(0, (Number(next.routeOffsetMinutes) || 0) - nextOffset);
  // Finish earlier stop with enough dwell + drive to still hit the later slot.
  let notAfter = new Date(
    nextStart.getTime() - (DRIVE_STOP_DWELL_MINUTES + legMin) * 60 * 1000,
  );

  // Also keep the earlier pickup before the later stop’s leave-home (e.g. 19:54).
  // That is the user’s “still at home / can insert earlier restaurants” deadline.
  if (next.leaveByAt) {
    const leaveHome = new Date(next.leaveByAt);
    if (!Number.isNaN(leaveHome.getTime()) && leaveHome < notAfter) {
      notAfter = leaveHome;
    }
  }

  return notAfter.toISOString();
}

function endpointLabel(point) {
  return String(placeLabel(point) || "").trim();
}

function labelIsShopName(point, restaurantName) {
  const label = endpointLabel(point).toLowerCase();
  const name = String(restaurantName || "").trim().toLowerCase();
  return Boolean(label && name && label === name);
}

/** Global Drive draft whose To is this restaurant (street on the draft, name on the card). */
function inProcessDraftForRestaurant(restaurantId, restaurantName, restaurantAddress) {
  const id = rid(restaurantId);
  const name = String(restaurantName || "").trim().toLowerCase();
  const address = String(restaurantAddress || "").trim().toLowerCase();
  return (
    loadCurrentDriveTrips().find((trip) => {
      if (!isInProcessDriveTrip(trip) || !endpointLabel(trip.origin)) return false;
      const destId = String(trip.destination?.restaurantId || "").trim();
      if (id && destId && destId === id) return true;
      const destLabel = endpointLabel(trip.destination).toLowerCase();
      if (name && destLabel === name) return true;
      if (
        address &&
        destLabel &&
        (destLabel === address || destLabel.includes(address) || address.includes(destLabel))
      ) {
        return true;
      }
      return false;
    }) || null
  );
}

function planIsOnlyThisPickup(plan, restaurantId) {
  const paid = getPaidStops(plan);
  const id = rid(restaurantId);
  if (!paid.length) return true;
  return paid.every((stop) => rid(stop.restaurantId) === id);
}

function restaurantDestinationPoint(restaurantId, restaurantAddress, restaurantName) {
  const address = String(restaurantAddress || "").trim();
  if (!address || labelIsShopName({ formattedAddress: address }, restaurantName)) return null;
  return {
    formattedAddress: address,
    address,
    ...(restaurantId ? { restaurantId: rid(restaurantId) } : {}),
  };
}

/**
 * Near me Drive has no later To. A street that is not this restaurant is a
 * leftover Global Drive destination and must stay. The restaurant's own street
 * is the pickup, even when the stop only stored the shop name.
 */
function nearMeDestinationIsAnotherPlace(plan, stop) {
  if (!stop) return false;
  if (plan?.endsAtRestaurant) return false;
  const destLabel = placeLabel(plan?.destination) || "";
  const stopAddress = String(stop.address || "").trim();
  if (!stopAddress || !destLabel) return false;
  if (rid(plan?.destination?.restaurantId) && rid(plan.destination.restaurantId) === rid(stop.restaurantId)) {
    return false;
  }
  return !sameDriveAddress(destLabel, stopAddress) && !sameDriveAddress(destLabel, stop.name);
}

/**
 * One Near me restaurant: the drive ends at that pickup. Drop the extra hop
 * that treated the restaurant street as a second stop.
 */
export function collapseNearMeRestaurantDestination(plan) {
  if (!plan || !isNearMeExploreMode()) return plan;
  const paid = getPaidStops(plan);
  if (paid.length !== 1) return plan;
  const stop = paid[0];
  if (nearMeDestinationIsAnotherPlace(plan, stop)) return plan;

  const next = { ...plan };
  let changed = false;
  if (!next.endsAtRestaurant) {
    next.endsAtRestaurant = true;
    changed = true;
  }

  const destLabel = placeLabel(next.destination) || "";
  const stopAddress = String(stop.address || "").trim();
  const street = stopAddress || (labelIsShopName({ formattedAddress: destLabel }, stop.name) ? "" : destLabel);
  if (street) {
    const pickup = {
      ...(next.destination && typeof next.destination === "object" ? next.destination : {}),
      formattedAddress: street,
      address: street,
      ...(rid(stop.restaurantId) ? { restaurantId: rid(stop.restaurantId) } : {}),
    };
    const sameDest =
      endpointLabel(next.destination).toLowerCase() === street.toLowerCase() &&
      rid(next.destination?.restaurantId) === rid(stop.restaurantId);
    if (!sameDest) {
      next.destination = pickup;
      changed = true;
    }
  } else if (rid(stop.restaurantId) && rid(next.destination?.restaurantId) !== rid(stop.restaurantId)) {
    next.destination = {
      ...(next.destination && typeof next.destination === "object" ? next.destination : {}),
      restaurantId: rid(stop.restaurantId),
    };
    changed = true;
  }

  if (!stopAddress && destLabel && !labelIsShopName({ formattedAddress: destLabel }, stop.name)) {
    next.stops = (next.stops || []).map((row) =>
      rid(row.restaurantId) === rid(stop.restaurantId) && !String(row.address || "").trim()
        ? { ...row, address: destLabel }
        : row,
    );
    changed = true;
  }

  const legs = Array.isArray(next.roadLegMinutes) ? next.roadLegMinutes : [];
  if (legs.length > 1) {
    next.roadLegMinutes = [Number(legs[0])].filter((minutes) => Number.isFinite(minutes) && minutes > 0);
    if (next.roadLegMinutes.length === 1) next.routeDurationMinutes = next.roadLegMinutes[0];
    changed = true;
  }

  return changed ? next : plan;
}

/** Near me has no separate To. The pickup restaurant is the end of the drive. */
function endNearMeTripAtRestaurant(plan, { restaurantId, restaurantName, restaurantAddress } = {}) {
  if (!plan || !isNearMeExploreMode() || !planIsOnlyThisPickup(plan, restaurantId)) return plan;
  const pickup = restaurantDestinationPoint(restaurantId, restaurantAddress, restaurantName);
  let next = plan;
  if (pickup) {
    const sameLabel = endpointLabel(plan.destination).toLowerCase() === endpointLabel(pickup).toLowerCase();
    const sameId = rid(plan.destination?.restaurantId) === rid(pickup.restaurantId);
    if (!sameLabel || !sameId) {
      next = {
        ...plan,
        destination: {
          ...(sameLabel && plan.destination && typeof plan.destination === "object" ? plan.destination : {}),
          ...pickup,
        },
      };
    }
  }
  return collapseNearMeRestaurantDestination(next);
}

/** True when the last pickup is the end of the drive, so the timeline has no To. */
export function driveTripEndsAtRestaurant(plan) {
  if (!plan) return false;
  const paid = getPaidStops(plan);
  const last = paid[paid.length - 1];
  if (!last) return false;
  if (plan.endsAtRestaurant && paid.length === 1) return true;
  if (destinationIsRestaurantStop(plan.destination, last)) return true;
  // A To is shown only after the customer set one on Global Drive.
  // An older saved street is not a stop after this restaurant.
  if (!plan.destinationChosen) return true;
  if (!isNearMeExploreMode() || paid.length !== 1) return false;
  return !nearMeDestinationIsAnotherPlace(plan, last);
}

/**
 * Replace a missing From/To, or a From/To that is only the restaurant name,
 * with the Global Drive draft (real departure → street).
 * Near me never copies a previous Global Drive To.
 */
function withRealDriveEndpoints(plan, { restaurantId, restaurantName, restaurantAddress } = {}) {
  if (!plan) return plan;
  if (isNearMeExploreMode()) {
    return endNearMeTripAtRestaurant(plan, { restaurantId, restaurantName, restaurantAddress });
  }
  const draft = inProcessDraftForRestaurant(restaurantId, restaurantName, restaurantAddress);
  const activeTrip = draft ? null : loadActiveDriveTrip();
  const source = draft || (endpointLabel(activeTrip?.origin) ? activeTrip : null);
  if (!source?.origin) return plan;
  const next = { ...plan };
  let changed = false;
  if (!endpointLabel(next.origin) || labelIsShopName(next.origin, restaurantName)) {
    if (!labelIsShopName(source.origin, restaurantName)) {
      next.origin = source.origin;
      changed = true;
    }
  }
  const destPlaceholder =
    !endpointLabel(next.destination) || labelIsShopName(next.destination, restaurantName);
  if (destPlaceholder && endpointLabel(source.destination) && !labelIsShopName(source.destination, restaurantName)) {
    next.destination = source.destination;
    if (source.destinationChosen) next.destinationChosen = true;
    changed = true;
  } else if (destPlaceholder && String(restaurantAddress || "").trim()) {
    const address = String(restaurantAddress).trim();
    next.destination = {
      formattedAddress: address,
      address,
      ...(restaurantId ? { restaurantId: rid(restaurantId) } : {}),
    };
    changed = true;
  }
  if (!next.departureIso && source.departureIso) {
    next.departureIso = source.departureIso;
    changed = true;
  }
  if (
    !(Number(next.routeDurationMinutes) > 0) &&
    Number(source.routeDurationMinutes) > 0
  ) {
    next.routeDurationMinutes = Number(source.routeDurationMinutes);
    changed = true;
  }
  return changed ? next : plan;
}

/**
 * Ensure a multi-stop plan exists and register/open this restaurant as the current stop.
 * Enforces: finish open stop before adding another; max 3.
 * After the trip has started, next stops must be further along the route.
 * Before departure / first pickup, any unbooked restaurant on the route is allowed
 * (stops are kept in route order for smooth collection).
 */
export function ensureOpenDriveStop({
  shop,
  origin,
  destination,
  departureIso,
  routeIndex,
  routeDurationMinutes,
}) {
  const restaurantId = rid(shop?.ownerId || shop?._id);
  if (!restaurantId) return { ok: false, reason: "missing_restaurant" };

  const shopLabel = shopName(shop);
  const shopAddress = String(shop?.shopDetails?.address || shop?.address || "").trim();
  const draftForShop = inProcessDraftForRestaurant(restaurantId, shopLabel, shopAddress);
  let resolvedOrigin = origin;
  let resolvedDestination = destination;
  if (draftForShop) {
    if (!endpointLabel(resolvedOrigin) || labelIsShopName(resolvedOrigin, shopLabel)) {
      resolvedOrigin = draftForShop.origin;
    }
    if (
      (!endpointLabel(resolvedDestination) || labelIsShopName(resolvedDestination, shopLabel)) &&
      endpointLabel(draftForShop.destination) &&
      !labelIsShopName(draftForShop.destination, shopLabel)
    ) {
      resolvedDestination = draftForShop.destination;
    }
  }

  let plan = loadDriveMultiStopPlan();
  const offset = Number(shop?.smartRoute?.routeOffsetMinutes);
  const routeOffsetMinutes = Number.isFinite(offset) ? offset : 0;
  const durationMin = Number(routeDurationMinutes);

  if (!plan) {
    plan = {
      id: `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      origin: resolvedOrigin || null,
      destination: resolvedDestination || null,
      departureIso: departureIso || null,
      routeIndex: routeIndex != null ? Number(routeIndex) : 0,
      routeDurationMinutes: Number.isFinite(durationMin) ? durationMin : null,
      stops: [],
    };
  } else {
    if (!plan.id) {
      plan.id = `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
    if (resolvedOrigin) plan.origin = resolvedOrigin;
    if (resolvedDestination) plan.destination = resolvedDestination;
    if (departureIso) plan.departureIso = departureIso;
    if (routeIndex != null) plan.routeIndex = Number(routeIndex);
    if (Number.isFinite(durationMin)) plan.routeDurationMinutes = durationMin;
  }

  const existing = getOrderedStops(plan).find((s) => rid(s.restaurantId) === restaurantId);
  if (existing) {
    if (existing.status === "taken") {
      return { ok: false, reason: "already_ordered", plan };
    }
    if (existing.status === "ordered" || existing.status === "ready") {
      if (!canAddMoreItemsAtDriveStop(plan, restaurantId)) {
        return { ok: false, reason: "already_ordered", plan };
      }
      // Same stop, more items: reopen without consuming another of the 3 stop slots.
      const reopened = {
        ...existing,
        status: "open",
        previousOrderIds: [
          ...(Array.isArray(existing.previousOrderIds) ? existing.previousOrderIds : []),
          ...(existing.orderId ? [existing.orderId] : []),
        ].filter(Boolean),
      };
      plan.stops = getOrderedStops(plan).map((s) =>
        rid(s.restaurantId) === restaurantId ? reopened : s,
      );
      saveDriveMultiStopPlan(plan);
      const ordered = getOrderedStops(plan);
      const stopIndex = ordered.findIndex((s) => rid(s.restaurantId) === restaurantId);
      return { ok: true, plan, stop: reopened, stopIndex, isAddOn: true };
    }
    // Resume open stop
    saveDriveMultiStopPlan(plan);
    return { ok: true, plan, stop: existing, stopIndex: getOrderedStops(plan).indexOf(existing) };
  }

  // Unfinished open stop at another restaurant: drop it (or restore add-on) so
  // the user can freely switch without being forced to finish first.
  const open = getActiveOpenStop(plan);
  if (open && rid(open.restaurantId) !== restaurantId) {
    const afterAbandon = abandonOpenDriveStops(plan);
    plan = afterAbandon || {
      origin: plan.origin || resolvedOrigin || null,
      destination: plan.destination || resolvedDestination || null,
      departureIso: plan.departureIso || departureIso || null,
      routeIndex: plan.routeIndex != null ? Number(plan.routeIndex) : routeIndex != null ? Number(routeIndex) : 0,
      routeDurationMinutes: Number.isFinite(plan.routeDurationMinutes)
        ? plan.routeDurationMinutes
        : Number.isFinite(durationMin)
          ? durationMin
          : null,
      stops: [],
    };
    if (resolvedOrigin) plan.origin = resolvedOrigin;
    if (resolvedDestination) plan.destination = resolvedDestination;
    if (departureIso) plan.departureIso = departureIso;
    if (routeIndex != null) plan.routeIndex = Number(routeIndex);
    if (Number.isFinite(durationMin)) plan.routeDurationMinutes = durationMin;
  }

  const paid = getPaidStops(plan);
  if (paid.length >= 1) {
    return { ok: false, reason: "one_restaurant", plan };
  }

  if (hasDrivePickupStarted(plan)) {
    return { ok: false, reason: "pickup_started", plan };
  }

  if (paid.length && mustPickFurtherAlongOnly(plan)) {
    const lastPaid = paid[paid.length - 1];
    const lastOffset = Number(lastPaid.routeOffsetMinutes) || 0;
    if (routeOffsetMinutes + 0.5 < lastOffset) {
      return { ok: false, reason: "must_be_further_along", plan, lastPaid };
    }
  }

  const driveFromDeparture = getDriveMinutesFromDeparture(shop);
  const stop = {
    restaurantId,
    name: shopName(shop),
    shopType: String(shop?.type || "").toLowerCase() === "boulangerie" ? "boulangerie" : "restaurant",
    routeOffsetMinutes,
    /** Actual drive from departure address (shop card ETA) — not along-route offset. */
    driveMinutes:
      Number.isFinite(driveFromDeparture) && driveFromDeparture > 0
        ? Math.round(driveFromDeparture)
        : null,
    status: "open",
    orderId: null,
    pickupWindow: null,
    pickupDate: null,
    leaveByLabel: null,
    leaveByAt: null,
    slotEndIso: null,
    address: String(shop?.shopDetails?.address || shop?.address || "").trim() || null,
    lat: (() => {
      const v = Number(
        shop?.lat ?? shop?.latitude ?? shop?.shopDetails?.lat ?? shop?.location?.lat,
      );
      return Number.isFinite(v) ? v : null;
    })(),
    lng: (() => {
      const v = Number(
        shop?.lng ?? shop?.longitude ?? shop?.shopDetails?.lng ?? shop?.location?.lng,
      );
      return Number.isFinite(v) ? v : null;
    })(),
  };

  plan.stops = [...getOrderedStops(plan).filter((s) => rid(s.restaurantId) !== restaurantId), stop];
  saveDriveMultiStopPlan(plan);
  const ordered = getOrderedStops(plan);
  const stopIndex = ordered.findIndex((s) => rid(s.restaurantId) === restaurantId);
  return { ok: true, plan, stop, stopIndex };
}

/**
 * Resolve From/To for a Drive trip when the live plan was lost (online payment redirect,
 * refresh, or checkout without ensureOpenDriveStop).
 */
function resolveDrivePlanEndpoints() {
  const active = loadDriveMultiStopPlan();
  if (active?.origin) {
    return {
      origin: active.origin,
      destination: active.destination || null,
      departureIso: active.departureIso || null,
      routeIndex: active.routeIndex != null ? Number(active.routeIndex) : 0,
      routeDurationMinutes:
        Number.isFinite(Number(active.routeDurationMinutes)) &&
        Number(active.routeDurationMinutes) > 0
          ? Number(active.routeDurationMinutes)
          : null,
      id: active.id || null,
    };
  }
  const inProcess = loadCurrentDriveTrips().find((t) => isInProcessDriveTrip(t));
  if (inProcess?.origin) {
    return {
      origin: inProcess.origin,
      destination: inProcess.destination || null,
      departureIso: inProcess.departureIso || null,
      routeIndex: inProcess.routeIndex != null ? Number(inProcess.routeIndex) : 0,
      routeDurationMinutes:
        Number.isFinite(Number(inProcess.routeDurationMinutes)) &&
        Number(inProcess.routeDurationMinutes) > 0
          ? Number(inProcess.routeDurationMinutes)
          : null,
      id: null,
    };
  }
  const trip = loadActiveDriveTrip() || loadLastTravelTrip();
  if (trip?.origin) {
    return {
      origin: trip.origin,
      destination: trip.destination || null,
      departureIso: trip.departureIso || null,
      routeIndex: trip.routeIndex != null ? Number(trip.routeIndex) : 0,
      routeDurationMinutes:
        Number.isFinite(Number(trip.routeDurationMinutes)) &&
        Number(trip.routeDurationMinutes) > 0
          ? Number(trip.routeDurationMinutes)
          : null,
      id: null,
    };
  }
  return null;
}

function isOpenDriveOrderForTrip(order) {
  if (!order) return false;
  const status = String(order.status || "").toLowerCase();
  if (!status || status === "completed" || status === "cancelled" || status === "hold") {
    return false;
  }
  const ot = String(order.orderType || "").toUpperCase();
  return (
    ot === "DRIVE" ||
    Boolean(order.driveLeaveHomeAt) ||
    Boolean(order.drivePickupTimingSnapshot?.leaveByLabel) ||
    Boolean(order.drivePickupTimingSnapshot?.slotLabel)
  );
}

/**
 * Attach a paid Drive order to the live plan / Current trips.
 * Safe to call after online payment redirect or when recovering orphan orders.
 */
export function attachPaidDriveOrderToCurrentTrips(order, hints = {}) {
  if (!order?._id && !hints.orderId) {
    return { plan: null, complete: true, nextStopAllowed: false, attached: false };
  }
  const orderId = String(hints.orderId || order._id || "").trim();
  if (!orderId) {
    return { plan: null, complete: true, nextStopAllowed: false, attached: false };
  }
  const restaurantId = rid(
    hints.restaurantId ||
      order.restaurantId ||
      order.restaurant?._id ||
      order.restaurant?.ownerId ||
      order.ownerId,
  );
  const restaurantName =
    hints.restaurantName ||
    order.restaurantName ||
    order.restaurant?.shopDetails?.shopName ||
    order.restaurant?.shopName ||
    null;
  const departure = driveDeparturePoint(order, restaurantId);
  const existingTrip = findDriveTripPlanForOrderId(orderId);
  // History-only matches are not "current" — re-attach open orders to Current trips.
  if (existingTrip && existingTrip.source !== "history") {
    let repaired = withCustomerDeparture(existingTrip.plan, departure, restaurantName);
    const originMoved = !sameDriveAddress(
      placeLabel(existingTrip.plan?.origin),
      placeLabel(repaired?.origin),
    );
    const endedAtRestaurant = withRealDriveEndpoints(repaired, {
      restaurantId,
      restaurantName,
      restaurantAddress: hints.restaurantAddress || order.restaurantAddress || null,
    });
    if (endedAtRestaurant && endedAtRestaurant !== repaired) repaired = endedAtRestaurant;
    if (originMoved && repaired) {
      repaired = withCheckoutDriveTiming(
        {
          ...repaired,
          roadLegMinutes: [],
          routePolyline: [],
          routeDurationMinutes: null,
        },
        order,
      );
    }
    if (repaired !== existingTrip.plan) {
      if (existingTrip.source === "active") saveDriveMultiStopPlan(repaired);
      else if (existingTrip.parkedTripId) {
        updateCurrentDriveTrip(existingTrip.parkedTripId, {
          ...repaired,
          id: existingTrip.parkedTripId,
        });
      }
    }
    const plan = repaired !== existingTrip.plan ? repaired : loadDriveMultiStopPlan();
    return {
      plan,
      complete: false,
      nextStopAllowed: canAddAnotherDriveStop(plan),
      attached: true,
      alreadyAttached: true,
    };
  }

  const snap = order.drivePickupTimingSnapshot || {};
  const leaveByAt =
    hints.leaveByAt ||
    (order.driveLeaveHomeAt ? new Date(order.driveLeaveHomeAt).toISOString() : null);

  return markDriveStopOrdered({
    restaurantId,
    orderId,
    restaurantName,
    pickupWindow: hints.pickupWindow || order.pickupWindow || snap.slotLabel || null,
    pickupDate: hints.pickupDate || order.pickupDate || null,
    leaveByLabel: hints.leaveByLabel || snap.leaveByLabel || null,
    leaveByAt,
    driveMinutes: hints.driveMinutes ?? snap.driveMinutes ?? null,
    tokenNumber: hints.tokenNumber || order.tokenNumber || null,
    restaurantAddress: order.restaurantAddress || null,
    restaurantLat: order.restaurantLocation?.lat,
    restaurantLng: order.restaurantLocation?.lng,
    asAddOn: Array.isArray(order?.driveRelatedOrderIds) && order.driveRelatedOrderIds.length > 0,
    /** Last resort when From/To storage was cleared after payment. */
    fallbackPlaceLabel: restaurantName || null,
    fallbackOrigin: departure,
  });
}

/** Mark current open stop as ordered after successful payment. */
export function markDriveStopOrdered({
  restaurantId,
  orderId,
  pickupWindow,
  pickupDate,
  leaveByLabel,
  leaveByAt,
  driveMinutes,
  tokenNumber,
  restaurantName,
  asAddOn = false,
  fallbackPlaceLabel = null,
  fallbackOrigin = null,
  restaurantAddress = null,
  restaurantLat = null,
  restaurantLng = null,
}) {
  let plan = loadDriveMultiStopPlan();
  const placeholderEndpoints =
    !plan ||
    !endpointLabel(plan.origin) ||
    labelIsShopName(plan.origin, restaurantName) ||
    labelIsShopName(plan.destination, restaurantName);
  if (placeholderEndpoints) {
    const seeded = withRealDriveEndpoints(
      plan || {
        id: `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        origin: null,
        destination: null,
        departureIso: null,
        routeIndex: 0,
        routeDurationMinutes: null,
        stops: [],
      },
      { restaurantId, restaurantName, restaurantAddress },
    );
    if (seeded && endpointLabel(seeded.origin) && !labelIsShopName(seeded.origin, restaurantName)) {
      plan = seeded;
    }
  }
  // Checkout can succeed after online payment redirect / lost session plan — rebuild From/To.
  if (!plan) {
    const endpoints = resolveDrivePlanEndpoints();
    const departure = fallbackOrigin?.formattedAddress ? fallbackOrigin : null;
    const label = String(fallbackPlaceLabel || restaurantName || "").trim();
    const restaurantPlace = label ? { formattedAddress: label, address: label } : null;
    const sameDeparture =
      Boolean(departure) &&
      sameDriveAddress(placeLabel(endpoints?.origin), departure.formattedAddress);
    // A different From is a different trip. Do not keep the Global Drive To or its drive time.
    if (endpoints?.origin && (!departure || sameDeparture)) {
      plan = withCustomerDeparture(
        {
          id: endpoints.id || `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          origin: endpoints.origin,
          destination: endpoints.destination || endpoints.origin,
          departureIso: endpoints.departureIso || null,
          routeIndex: endpoints.routeIndex != null ? Number(endpoints.routeIndex) : 0,
          routeDurationMinutes: endpoints.routeDurationMinutes,
          stops: [],
        },
        departure,
        restaurantName,
      );
    } else if (departure) {
      plan = {
        id: `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        origin: departure,
        destination: restaurantPlace || departure,
        departureIso: null,
        routeIndex: 0,
        routeDurationMinutes: null,
        stops: [],
      };
    } else if (restaurantPlace) {
      // Minimal single-stop trip so Current trips still shows the paid order.
      plan = {
        id: `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        origin: restaurantPlace,
        destination: restaurantPlace,
        departureIso: null,
        routeIndex: 0,
        routeDurationMinutes: null,
        stops: [],
      };
    } else {
      return { plan: null, complete: true, nextStopAllowed: false, attached: false };
    }
  }

  const originBeforeRepair = placeLabel(plan?.origin);
  const repairedOrigin = withCustomerDeparture(plan, fallbackOrigin, restaurantName);
  if (repairedOrigin && repairedOrigin !== plan) plan = repairedOrigin;
  const originMoved =
    Boolean(placeLabel(plan?.origin)) && placeLabel(plan?.origin) !== originBeforeRepair;
  if (originMoved && planIsOnlyThisPickup(plan, restaurantId)) {
    const pickup = restaurantDestinationPoint(restaurantId, restaurantAddress, restaurantName);
    if (pickup) {
      plan = {
        ...plan,
        destination: pickup,
        destinationChosen: false,
        endsAtRestaurant: true,
        roadLegMinutes: [],
        routePolyline: [],
        routeDurationMinutes: null,
      };
    }
  }
  dismissInProcessDriveTripsExceptOrigin(plan?.origin);
  const repairedEndpoints = withRealDriveEndpoints(plan, {
    restaurantId,
    restaurantName,
    restaurantAddress,
  });
  if (repairedEndpoints && repairedEndpoints !== plan) plan = repairedEndpoints;

  const id = rid(restaurantId);
  if (!id) return { plan, complete: true, nextStopAllowed: false, attached: false };

  const nextToken = String(tokenNumber || "").trim();
  let nextLeaveByAt = null;
  if (leaveByAt) {
    const d = new Date(leaveByAt);
    if (!Number.isNaN(d.getTime())) nextLeaveByAt = d.toISOString();
  }
  const nextDrive = Number(driveMinutes);

  const existingStops = getOrderedStops(plan);
  const datesMatch = (a, b) => {
    const da = String(a || "").trim();
    const db = String(b || "").trim();
    if (!da || !db) return true;
    return da === db;
  };
  const sameDayStop = existingStops.find(
    (s) => rid(s.restaurantId) === id && datesMatch(s.pickupDate, pickupDate),
  );
  const openUnpaidStop =
    sameDayStop &&
    sameDayStop.status === "open" &&
    !getStopOrderIds(sameDayStop).length;

  const freshGroup = (ids, tokens, window, date, status = "ordered") => ({
    key: ids.map((x) => rid(x)).filter(Boolean).sort().join("__") || "order",
    orderIds: ids.map((x) => rid(x)).filter(Boolean),
    tokenNumbers: tokens,
    status,
    pickupWindow: window || null,
    pickupDate: date || null,
  });

  // Checkout can succeed without ensureOpenDriveStop (e.g. full list while Drive
  // route results are hidden). Still attach the paid order to Current trips.
  if (!sameDayStop || openUnpaidStop) {
    const sameRestaurantOtherDay = existingStops.some((s) => rid(s.restaurantId) === id);
    if (!sameRestaurantOtherDay && getPaidStops(plan).length >= 1) {
      return {
        plan,
        complete: false,
        nextStopAllowed: false,
        paidCount: getPaidStops(plan).length,
        attached: false,
      };
    }
    const tokenNumbers = nextToken ? [nextToken] : [];
    const orderIds = orderId ? [String(orderId)] : [];
    plan.stops = [
      ...existingStops.filter((s) => !openUnpaidStop || s !== sameDayStop),
      {
        restaurantId: id,
        name: String(restaurantName || "").trim() || "Restaurant",
        status: "ordered",
        orderId: orderId || null,
        previousOrderIds: [],
        pickupWindow: pickupWindow || null,
        pickupDate: pickupDate || null,
        leaveByLabel: leaveByLabel || null,
        leaveByAt: nextLeaveByAt,
        tokenNumber: tokenNumbers[0] || null,
        tokenNumbers,
        pickupGroups: orderIds.length
          ? [freshGroup(orderIds, tokenNumbers, pickupWindow, pickupDate)]
          : [],
        driveMinutes:
          Number.isFinite(nextDrive) && nextDrive > 0 ? Math.round(nextDrive) : null,
        routeOffsetMinutes: 0,
        slotEndIso: slotEndIsoFromWindow(pickupWindow, pickupDate),
        address: String(restaurantAddress || "").trim() || null,
        lat: Number.isFinite(Number(restaurantLat)) ? Number(restaurantLat) : null,
        lng: Number.isFinite(Number(restaurantLng)) ? Number(restaurantLng) : null,
      },
    ];
  } else if (!asAddOn) {
    const ids = getStopOrderIds(sameDayStop);
    // A new order is another trip and another token, even with the same From/To.
    // Only "add more items" (asAddOn) adds a second token on this trip.
    if (!(orderId && ids.includes(String(orderId)))) {
      const tokenNumbers = nextToken ? [nextToken] : [];
      const orderIds = orderId ? [String(orderId)] : [];
      const parked = {
        ...plan,
        id: `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        stops: [
          {
            restaurantId: id,
            name: String(restaurantName || sameDayStop.name || "").trim() || "Restaurant",
            status: "ordered",
            orderId: orderId || null,
            previousOrderIds: [],
            pickupWindow: pickupWindow || null,
            pickupDate: pickupDate || null,
            leaveByLabel: leaveByLabel || null,
            leaveByAt: nextLeaveByAt,
            tokenNumber: tokenNumbers[0] || null,
            tokenNumbers,
            pickupGroups: orderIds.length
              ? [freshGroup(orderIds, tokenNumbers, pickupWindow, pickupDate)]
              : [],
            driveMinutes:
              Number.isFinite(nextDrive) && nextDrive > 0 ? Math.round(nextDrive) : null,
            routeOffsetMinutes: sameDayStop.routeOffsetMinutes || 0,
            slotEndIso: slotEndIsoFromWindow(pickupWindow, pickupDate),
            address: String(restaurantAddress || sameDayStop.address || "").trim() || null,
            lat: Number.isFinite(Number(restaurantLat)) ? Number(restaurantLat) : sameDayStop.lat ?? null,
            lng: Number.isFinite(Number(restaurantLng)) ? Number(restaurantLng) : sameDayStop.lng ?? null,
          },
        ],
        tripStartedAt: null,
        inProcess: false,
      };
      delete parked.navigationOpenedAt;
      parkDriveTrip(parked);
    }
  } else {
    plan.stops = existingStops.map((s) => {
      if (s !== sameDayStop) return s;
      const previousOrderIds = [
        ...(Array.isArray(s.previousOrderIds) ? s.previousOrderIds : []),
        ...(s.orderId && String(s.orderId) !== String(orderId || "") ? [s.orderId] : []),
      ].filter(Boolean);
      const nextWindow = pickupWindow || s.pickupWindow;
      const nextDate = pickupDate || s.pickupDate;
      const leaveAt = nextLeaveByAt || s.leaveByAt || null;
      const existingTokens = getStopTokenNumbers(s);
      const tokenNumbers =
        nextToken && !existingTokens.includes(nextToken)
          ? [...existingTokens, nextToken]
          : existingTokens.length
            ? existingTokens
            : nextToken
              ? [nextToken]
              : [];
      const groups = pickupGroupsOf(s);
      const mergedIds = [...new Set([...getStopOrderIds(s), orderId].filter(Boolean).map(String))];
      const pickupGroups =
        groups.length <= 1
          ? [freshGroup(mergedIds, tokenNumbers, nextWindow, nextDate, "ordered")]
          : [
              ...groups.slice(0, -1),
              freshGroup(
                [...new Set([...(groups[groups.length - 1].orderIds || []), orderId].filter(Boolean).map(String))],
                tokenNumbers,
                nextWindow,
                nextDate,
                "ordered",
              ),
            ];
      return {
        ...s,
        status: "ordered",
        orderId: orderId || s.orderId,
        previousOrderIds,
        pickupWindow: nextWindow,
        pickupDate: nextDate,
        leaveByLabel: leaveByLabel || s.leaveByLabel,
        leaveByAt: leaveAt,
        tokenNumber: pickupGroups.length === 1 ? tokenNumbers[tokenNumbers.length - 1] || nextToken || null : null,
        tokenNumbers: pickupGroups.length === 1 ? tokenNumbers : [],
        pickupGroups,
        driveMinutes:
          Number.isFinite(nextDrive) && nextDrive > 0
            ? Math.round(nextDrive)
            : s.driveMinutes ?? null,
        slotEndIso: slotEndIsoFromWindow(nextWindow, nextDate),
      };
    });
  }

  saveDriveMultiStopPlan(plan);
  clearSmartRouteSlot(id);
  clearInProcessDriveTripForEndpoints({
    origin: plan.origin,
    destination: plan.destination,
    restaurantId: id,
  });
  notifyMultiStopPlanUi();
  notifyTripHistoryUi();
  try {
    // Persist paid stop to server ASAP so website/PWA can see the trip.
    window.dispatchEvent(new Event("smartRoute:driveTripFlush"));
  } catch {
    /* ignore */
  }

  const paidCount = getPaidStops(plan).length;
  const complete = paidCount >= MAX_DRIVE_STOPS;
  const nextStopAllowed = !complete && canAddAnotherDriveStop(plan);

  return { plan, complete, nextStopAllowed, paidCount, attached: paidCount > 0 };
}

export function getDriveStopBadge(plan, restaurantId) {
  if (!plan) return null;
  const ordered = getOrderedStops(plan);
  const idx = ordered.findIndex((s) => rid(s.restaurantId) === rid(restaurantId));
  if (idx < 0) return null;
  const stop = ordered[idx];
  const canAddMore = canAddMoreItemsAtDriveStop(plan, restaurantId);
  return {
    index: idx + 1,
    status: stop.status,
    canAddMore,
    label:
      stop.status === "taken"
        ? `Stop ${idx + 1} ✓`
        : stop.status === "ready"
          ? `Stop ${idx + 1} · Ready`
          : stop.status === "ordered"
            ? canAddMore
              ? `Stop ${idx + 1} ✓ · +`
              : `Stop ${idx + 1} ✓`
            : `Stop ${idx + 1}`,
  };
}

/** Coarse place equality for detecting a new Drive trip (From/To changed). */
export function driveEndpointsMatch(a, b) {
  if (!a || !b) return false;
  const latA = Number(a.lat);
  const lngA = Number(a.lng);
  const latB = Number(b.lat);
  const lngB = Number(b.lng);
  if (
    Number.isFinite(latA) &&
    Number.isFinite(lngA) &&
    Number.isFinite(latB) &&
    Number.isFinite(lngB)
  ) {
    return Math.abs(latA - latB) < 1e-3 && Math.abs(lngA - lngB) < 1e-3;
  }
  const labelA = String(a.formattedAddress || a.address || "")
    .trim()
    .toLowerCase();
  const labelB = String(b.formattedAddress || b.address || "")
    .trim()
    .toLowerCase();
  return Boolean(labelA && labelB && labelA === labelB);
}

/**
 * Move a parked Current trip back into the live working plan (for Continue on route).
 * Parks any other active paid plan first so nothing is lost.
 */
export function promoteParkedDriveTripToActive(tripId) {
  const id = String(tripId || "");
  if (!id) return { ok: false, plan: null };

  const parked = loadCurrentDriveTrips().find(
    (t) => String(t.id) === id && !isInProcessDriveTrip(t),
  );
  if (!parked || !getPaidStops(parked).length) {
    return { ok: false, plan: null };
  }

  const active = loadDriveMultiStopPlan();
  if (active && getPaidStops(active).length && String(active.id || "") !== id) {
    if (!active.leaveByLabel) {
      active.leaveByLabel = getTripLeaveHomeLabel(active);
    }
    if (!active.id) {
      active.id = `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
    parkDriveTrip(active);
  }

  clearDriveMultiStopPlan();
  removeCurrentDriveTrip(id);
  const next = saveDriveMultiStopPlan({
    ...parked,
    id,
    inProcess: false,
    status: undefined,
  });
  notifyTripHistoryUi();
  return { ok: Boolean(next), plan: next };
}

/**
 * If the user plans a different From/To while an active trip has paid stops,
 * treat it as another trip: keep the previous one in Current trips and clear
 * the working plan for the new route.
 * @param {{ origin?: object, destination?: object, force?: boolean }} [args]
 *        force — Add trip always parks the active paid trip (even same From/To).
 */
export function parkActiveDriveTripForNewEndpoints({ origin, destination, force = false } = {}) {
  const plan = loadDriveMultiStopPlan();
  const paid = getPaidStops(plan);
  if (!plan || !paid.length) {
    return { parked: false, plan: plan || null };
  }
  const sameOrigin = driveEndpointsMatch(plan.origin, origin);
  const sameDest = driveEndpointsMatch(plan.destination, destination);
  if (!force && sameOrigin && sameDest) {
    return { parked: false, plan };
  }

  if (!plan.leaveByLabel) {
    plan.leaveByLabel = getTripLeaveHomeLabel(plan);
  }
  // Stable id so re-parking the same working plan updates instead of duplicating.
  if (!plan.id) {
    plan.id = `current-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  parkDriveTrip(plan);
  clearDriveMultiStopPlan();
  notifyTripHistoryUi();
  try {
    window.dispatchEvent(new Event("smartRoute:driveTripFlush"));
  } catch {
    /* ignore */
  }
  return { parked: true, plan: null };
}

/** @deprecated use parkActiveDriveTripForNewEndpoints */
export function archiveActiveDriveTripForNewEndpoints(args) {
  return parkActiveDriveTripForNewEndpoints(args);
}

/** Restaurant ids already ordered on today's Drive trips (active + recent). */
export function getRecentDriveOrderedRestaurantIds({ withinMs = 12 * 60 * 60 * 1000 } = {}) {
  const ids = new Set();
  const cutoff = Date.now() - withinMs;

  for (const s of getPaidStops(loadDriveMultiStopPlan())) {
    if (s?.restaurantId) ids.add(rid(s.restaurantId));
  }

  for (const id of getCurrentDriveOrderedRestaurantIds()) {
    ids.add(id);
  }

  try {
    for (const trip of loadDriveTripHistory()) {
      const closed = trip?.closedAt ? new Date(trip.closedAt).getTime() : 0;
      if (closed && closed < cutoff) continue;
      for (const s of trip?.stops || []) {
        if (s?.restaurantId) ids.add(rid(s.restaurantId));
      }
    }
  } catch {
    /* ignore */
  }

  return ids;
}

/**
 * Badge for a shop ordered on another (recent) Drive trip — not the active plan.
 */
export function getPriorDriveOrderBadge(restaurantId, plan = loadDriveMultiStopPlan()) {
  const id = rid(restaurantId);
  if (!id) return null;
  if (getDriveStopBadge(plan, id)) return null;
  if (!getRecentDriveOrderedRestaurantIds().has(id)) return null;
  return {
    index: 0,
    status: "ordered",
    canAddMore: false,
    fromPriorTrip: true,
    label: "Ordered",
  };
}

/** Ensure paid stops stay visible/highlighted even if the new route API omits them. */
export function mergePaidDriveStopsIntoShops(shops, plan = loadDriveMultiStopPlan()) {
  const list = Array.isArray(shops) ? [...shops] : [];
  const paid = getPaidStops(plan);
  if (!paid.length) return list;

  const present = new Set(
    list.map((s) => rid(s?.ownerId || s?._id)).filter(Boolean),
  );

  for (const stop of paid) {
    const id = rid(stop.restaurantId);
    if (!id || present.has(id)) continue;
    present.add(id);
    list.push({
      ownerId: id,
      _id: id,
      type: stop.shopType === "boulangerie" ? "boulangerie" : "restaurant",
      shopDetails: {
        shopName: stop.name || "Restaurant",
        address: stop.address || "",
        lat: stop.lat,
        lng: stop.lng,
      },
      lat: stop.lat,
      lng: stop.lng,
      address: stop.address || "",
      smartRoute: {
        routeOffsetMinutes: Number(stop.routeOffsetMinutes) || 0,
        bestMatchSlot:
          stop.pickupWindow && stop.pickupDate
            ? {
                pickupWindow: stop.pickupWindow,
                date: stop.pickupDate,
                hasCapacity: true,
                slotRemainingOrders: null,
              }
            : null,
        bookableSlots: [],
        arrivalTime: null,
      },
      fromDrivePlanStop: true,
    });
  }

  return list;
}

/**
 * Also surface restaurants ordered on another recent Drive trip so they stay
 * highlighted when the user plans a new From/To.
 */
export function mergeRecentDriveOrdersIntoShops(shops, plan = loadDriveMultiStopPlan()) {
  let list = mergePaidDriveStopsIntoShops(shops, plan);
  const present = new Set(
    list.map((s) => rid(s?.ownerId || s?._id)).filter(Boolean),
  );
  const cutoff = Date.now() - 12 * 60 * 60 * 1000;

  try {
    for (const trip of [...loadCurrentDriveTrips(), ...loadDriveTripHistory()]) {
      const closed = trip?.closedAt ? new Date(trip.closedAt).getTime() : 0;
      const parkedAt = trip?.parkedAt ? new Date(trip.parkedAt).getTime() : 0;
      const stamp = closed || parkedAt || 0;
      if (stamp && stamp < cutoff) continue;
      for (const stop of trip?.stops || []) {
        const id = rid(stop.restaurantId);
        if (!id || present.has(id)) continue;
        // Skip if it's on the active plan (already merged above)
        if (getPaidStops(plan).some((s) => rid(s.restaurantId) === id)) continue;
        present.add(id);
        list.push({
          ownerId: id,
          _id: id,
          type: stop.shopType === "boulangerie" ? "boulangerie" : "restaurant",
          shopDetails: {
            shopName: stop.name || "Restaurant",
            address: stop.address || "",
            lat: stop.lat,
            lng: stop.lng,
          },
          lat: stop.lat,
          lng: stop.lng,
          address: stop.address || "",
          smartRoute: {
            routeOffsetMinutes: Number(stop.routeOffsetMinutes) || 0,
            bestMatchSlot: null,
            bookableSlots: [],
            arrivalTime: null,
          },
          fromPriorDriveTrip: true,
        });
      }
    }
  } catch {
    /* ignore */
  }

  return list;
}

/** True when pickup window starts at/after the chained not-before instant. */
export function isSlotAtOrAfterNotBefore(pickupWindow, date, notBeforeIso) {
  if (!notBeforeIso) return true;
  const start = parsePickupSlotInstant(pickupWindow, date);
  const floor = new Date(notBeforeIso);
  if (!start || Number.isNaN(floor.getTime())) return true;
  return start.getTime() >= floor.getTime() - NOT_BEFORE_TOLERANCE_MS;
}

function slotStillOpen(slot, bookable) {
  if (!slot?.pickupWindow || !slot?.date) return false;
  if (!bookable.length) return true;
  return bookable.some(
    (row) =>
      row.pickupWindow === slot.pickupWindow && String(row.date) === String(slot.date),
  );
}

/**
 * Pickup window stored when a Global Drive restaurant is opened.
 * A slot filter from the previous trip must not leave the new trip without a window:
 * use the filtered slot when this restaurant still has it, otherwise the best match,
 * otherwise the earliest bookable window on the new route.
 */
export function resolveDriveShopCheckoutSlot({
  preferred = null,
  bestMatch = null,
  bookableSlots = [],
  notBeforeIso = null,
  notAfterIso = null,
} = {}) {
  const bookable = (Array.isArray(bookableSlots) ? bookableSlots : []).filter(
    (slot) => slot?.hasCapacity && slot.pickupWindow && slot.date,
  );
  if (slotStillOpen(preferred, bookable)) return preferred;
  if (slotStillOpen(bestMatch, bookable)) return bestMatch;
  return pickEarliestBookableAfter(bookable, notBeforeIso, notAfterIso) || bookable[0] || null;
}

/** Earliest capacity slot at/after notBefore and at/before notAfter (route-chained timing). */
export function pickEarliestBookableAfter(bookableSlots, notBeforeIso, notAfterIso = null) {
  const slots = (Array.isArray(bookableSlots) ? bookableSlots : [])
    .filter((s) => s?.hasCapacity && s.pickupWindow && s.date)
    .sort((a, b) => `${a.date}|${a.pickupWindow}`.localeCompare(`${b.date}|${b.pickupWindow}`));
  if (!slots.length) return null;
  return (
    slots.find((s) => {
      if (!isSlotAtOrAfterNotBefore(s.pickupWindow, s.date, notBeforeIso)) return false;
      if (!notAfterIso) return true;
      const start = parsePickupSlotInstant(s.pickupWindow, s.date);
      const ceil = new Date(notAfterIso);
      if (!start || Number.isNaN(ceil.getTime())) return true;
      return start.getTime() <= ceil.getTime() + NOT_BEFORE_TOLERANCE_MS;
    }) || null
  );
}

/** Last paid stop’s route offset — next pickups should be further along once the trip has started. */
export function getLastPaidRouteOffset(plan) {
  const paid = getPaidStops(plan);
  if (!paid.length) return null;
  return Number(paid[paid.length - 1].routeOffsetMinutes) || 0;
}

/** Eligible for another Drive stop (not already locked; further-along only after trip started). */
export function isShopEligibleForNextDriveStop(shop, plan, now = new Date()) {
  if (!plan?.stops?.length) return true;
  const id = rid(shop?.ownerId || shop?._id);
  if (!id) return false;

  const existing = getOrderedStops(plan).find((s) => rid(s.restaurantId) === id);
  if (existing?.status === "ordered" || existing?.status === "ready" || existing?.status === "taken") {
    return canAddMoreItemsAtDriveStop(plan, id, now);
  }

  if (!canAddAnotherDriveStop(plan, now)) return false;

  // An unfinished open stop at another shop does not lock browsing — it will be
  // replaced when the user opens a different restaurant (see ensureOpenDriveStop).
  if (!mustPickFurtherAlongOnly(plan, now)) return true;

  const lastPaidOffset = getLastPaidRouteOffset(plan);
  if (lastPaidOffset == null) return true;

  const offset = Number(shop?.smartRoute?.routeOffsetMinutes) || 0;
  return offset + 0.5 >= lastPaidOffset;
}

export function partitionShopsForDriveMultiStop(shops, plan) {
  const list = Array.isArray(shops) ? shops : [];
  if (!plan?.stops?.length) {
    return { nextStops: list, bookedStops: [], passedStops: [], addMoreStops: [] };
  }
  const bookedStops = [];
  const addMoreStops = [];
  const nextStops = [];
  const passedStops = [];
  const tripStarted = mustPickFurtherAlongOnly(plan);
  const lastPaidOffset = getLastPaidRouteOffset(plan);

  for (const shop of list) {
    const id = rid(shop?.ownerId || shop?._id);
    const existing = getOrderedStops(plan).find((s) => rid(s.restaurantId) === id);
    if (existing?.status === "ordered" || existing?.status === "ready" || existing?.status === "taken") {
      if (canAddMoreItemsAtDriveStop(plan, id)) addMoreStops.push(shop);
      else bookedStops.push(shop);
      continue;
    }
    const offset = Number(shop?.smartRoute?.routeOffsetMinutes) || 0;
    if (
      tripStarted &&
      lastPaidOffset != null &&
      offset + 0.5 < lastPaidOffset
    ) {
      passedStops.push(shop);
      continue;
    }
    if (isShopEligibleForNextDriveStop(shop, plan)) nextStops.push(shop);
    else passedStops.push(shop);
  }

  const byOffset = (a, b) =>
    (Number(a?.smartRoute?.routeOffsetMinutes) || 0) -
    (Number(b?.smartRoute?.routeOffsetMinutes) || 0);

  return {
    nextStops: [...nextStops].sort(byOffset),
    addMoreStops: [...addMoreStops].sort(byOffset),
    bookedStops: [...bookedStops].sort(byOffset),
    passedStops: [...passedStops].sort(byOffset),
  };
}

/**
 * Sort Drive results along the corridor from origin (near From → toward To).
 */
export function sortShopsForDriveMultiStop(shops, _plan) {
  const list = Array.isArray(shops) ? [...shops] : [];
  return list.sort(
    (a, b) =>
      (Number(a?.smartRoute?.routeOffsetMinutes) || 0) -
        (Number(b?.smartRoute?.routeOffsetMinutes) || 0) ||
      String(a?.ownerId || a?._id || "").localeCompare(String(b?.ownerId || b?._id || "")),
  );
}
